import axios from "axios";
import { message } from "antd";
import { isPublicCandidateRoute } from "../utils/ssoCookie";
const { REACT_APP_API_URL } = process.env;

// Fetch the client's public IP fresh on every page load for audit logging.
// Stored only in memory so it always reflects the current network.
// Non-blocking - first few requests may not carry the header,
// but the server falls back to req.ip automatically.
let _clientIp = null;

const _fetchClientIp = () => {
  fetch("https://api.ipify.org?format=json")
    .then((r) => r.json())
    .then((d) => { _clientIp = d.ip || null; })
    .catch(() => { });
};

_fetchClientIp();

// Re-fetch when the browser reconnects to a different network so the stored IP
// stays current without requiring a full page refresh.
window.addEventListener("online", _fetchClientIp);

// Global request timeout. Without this, a stalled mobile connection (WiFi↔cellular
// handoff, weak signal) never rejects - the browser waits on the OS-level TCP
// timeout, which can be 15-20 minutes, during which the UI just spins. A finite
// timeout makes such requests fail fast so the user can retry.
axios.defaults.timeout = 120000; // 2 minutes

// Interceptors are registered exactly once (module load), NOT per API call.
// Previously they were registered inside makeAPICall, so every call stacked a
// fresh pair that was never ejected - they piled up unboundedly and re-ran their
// logic on every subsequent response.
let _interceptorsRegistered = false;
const registerAxiosInterceptors = () => {
  if (_interceptorsRegistered) return;
  _interceptorsRegistered = true;

  // Attach the auth token header to outgoing requests.
  axios.interceptors.request.use(
    (config) => {
      const accessToken = localStorage.getItem("accessToken");
      const ipHeader = _clientIp ? { "x-client-ip": _clientIp } : {};
      if (accessToken) {
        config.headers = {
          accept: "application/json",
          "content-type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: "Bearer " + accessToken,
          platform: "web-admin",
          ...ipHeader,
        };
      } else {
        config.headers = {
          accept: "application/json",
          "content-type": "application/json",
          platform: "web-admin",
          ...ipHeader,
        };
      }
      // FormData needs Content-Type with boundary - let the browser set it
      if (config.data instanceof FormData) {
        delete config.headers["content-type"];
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Refresh the token on an expired-token response.
  axios.interceptors.response.use(
    (response) => {
      if (response.data.code == 401) {
        // Public candidate-facing pages (opened from an emailed link) have no
        // real session - never bounce them to /signin on a stray 401.
        if (isPublicCandidateRoute(window.location.pathname)) {
          return response;
        }
        localStorage.removeItem("user_data");
        localStorage.removeItem("is_reporting_manager");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location = `/signin`;
      } else {
        return response;
      }
    },
    async function (error) {
      const originalRequest = error.config;
      let refreshToken = localStorage.getItem("refreshToken");
      if (
        refreshToken &&
        error?.response?.status === 401 &&
        !originalRequest._retry
      ) {
        if (originalRequest.url.includes("/refreshToken")) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          const url = Service.API_URL + Service.refreshToken;
          const response = await axios.post(url, {
            refreshToken: refreshToken,
          });
          if (response.status === 200 && response.data.authToken) {
            localStorage.setItem(
              "accessToken",
              response.data.authToken.accessToken
            );
            localStorage.setItem(
              "refreshToken",
              response.data.authToken.refreshToken
            );
            const res = await axios(originalRequest);
            return res;
          } else {
            return Promise.reject(response);
          }
        } catch (e) {
          return Promise.reject(e);
        }
      } else {
        return Promise.reject(error);
      }
    }
  );
};

export default class Service {
  static Server_Base_URL = REACT_APP_API_URL;
  static API_URL =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_URL + "/api"
      : `${this.Server_Base_URL}/api`;

  static API_Call_Counter = 0;
  static incre_API_Call_Counter = () => this.API_Call_Counter++;
  static decre_API_Call_Counter = () =>
  (this.API_Call_Counter =
    this.API_Call_Counter > 0 ? this.API_Call_Counter - 1 : 0);

  static error_message = "Something went wrong!";
  static error_message_key = "error_message_key";

  static message_containner = [];
  static add_message = (text) => {
    var index = this.message_containner.findIndex((x) => x === text);
    // here you can check specific property for an object whether it exist in your array or not
    if (index === -1) {
      this.message_containner.push(text);
    }
    return index;
  };
  static remove_message = (message) =>
  (this.message_containner = this.message_containner.filter(
    (m) => m !== message
  ));
  static messageError = (msg) => {
    const index = this.add_message(msg);
    if (index === -1) {
      message.error(msg).then(() => {
        this.remove_message(msg);
      });
    }
  };

  static messageInfo = (msg) => {
    const index = this.add_message(msg);
    if (index === -1) {
      message.info(msg).then(() => {
        this.remove_message(msg);
      });
    }
  };

  static postMethod = "POST";
  static getMethod = "GET";
  static putMethod = "PUT";
  static patchMethod = "PATCH";
  static deleteMethod = "DELETE";
  static headers = {
    accept: "application/json",
    "content-type": "application/json",
  };

  //Organisation-tree
  static organisationtree = "/emp/employeeTreeList";
  static mergechildren = "/emp/mergeEmployeeData";
  static getAdmindata = "/emp/getAdmindata";
  static fullOrgTree = "/emp/fullOrgTree";

  // Employee Data Form (pre-filled .docx)
  static employeeDataForm = "/emp/employeeDataForm";
  static joiningForm = "/emp/joiningForm";
  static discontinuationForm = "/emp/discontinuationForm";

  // Finance Report (Aadhaar / PAN / salary bank details, Branch from IFSC)
  static financeReportList = "/financeReport/list";
  static financeReportExport = "/financeReport/export";

  // New-joiner document pack (rendered on demand from pinned Document Master templates)
  static joiningDocumentsByEmployee = "/joiningDocuments/employee";
  static joiningDocumentDownload = "/joiningDocuments/download";

  //Saas flow API's company reg
  static registerAdminAndCompany = "/CompanyReg/registerAdminAndCompany";
  static verifyRegistration = "/CompanyReg/verify-registration";
  static smtpConfig = "/smtp_config/verifyAndSaveSMTP";
  static smtpGetConfig = "/smtp_config/getSmtpConfig";
  static smtpVerifyConnection = "/smtp_config/verifySmtp";
  /** Microsoft 365 / Outlook - POST body: outlookClientId, outlookRedirectUri, outlookClientSecret, fromName */
  static smtpOutlookCredentials = "/smtp_config/outlook/credentials";
  /** Microsoft 365 / Outlook OAuth - GET returns { data: { url } } */
  static smtpOutlookOAuthUrl = "/smtp_config/outlook/oauth-url";
  static smtpOutlookDisconnect = "/smtp_config/outlook/disconnect";
  static smtpOutlookTestEmail = "/smtp_config/outlook/test-email";
  static biomaxConfig = "/biomax_config/verifyAndSaveBiomax";
  static biomaxGetConfig = "/biomax_config/getBiomaxConfig";
  static dahuaConfig = "/dahua/config";
  static dahuaTestConnection = "/dahua/config/test-connection";
  static dahuaTestDbConnection = "/dahua/config/test-db-connection";
  static dahuaSyncPunches = "/dahua/config/sync";
  static genericDeviceConfigSave = "/generic-device/config/save";
  static genericDeviceConfigList = "/generic-device/config/list";
  static genericDeviceConfigTest = "/generic-device/config/test-connection";
  static zohoPayrollConfig = "/zoho-payroll/config";
  static zohoPayrollConnect = "/zoho-payroll/oauth/connect";
  static zohoPayrollAttendanceSync = "/zoho-payroll/attendance/cycle";
  static apiSettingsMcpGet = "/api-settings/mcp";
  static apiSettingsMcpGenerate = "/api-settings/mcp/generate";
  static fileSizeUpload = "/CompanyReg/file-upload-size";

  // company CRUD
  static addCompany = "/CompanyReg/addCompany";
  static listCompany = "/CompanyReg/getCompanyList";
  static editCompany = "/CompanyReg/editCompany";
  static updateEmailConfig = "/CompanyReg/updateEmailConfig";
  static deleteCompany = "/CompanyReg/deleteCompany";

  //Auth Module
  static login = "/admin/login";
  static loginWithToken = "/admin/login/token";
  static googleSSOLogin = "/admin/googleSSOLogin";
  static logout = "/admin/logout";
  static userById = "/admin/userById";
  static empById = "/emp/getempbyid";
  static refreshToken = "/auth/refreshToken";

  // Two-Factor Authentication (email OTP)
  static verifyMfa = "/admin/verify-mfa";
  static resendMfa = "/admin/resend-mfa";
  static getMfaSetting = "/admin/mfa-setting";
  static updateMfaSetting = "/admin/mfa-setting";
  static getSessionSetting = "/admin/session-setting";
  static updateSessionSetting = "/admin/session-setting";
  static getForcePasswordChangeSetting = "/admin/force-password-change-setting";
  static updateForcePasswordChangeSetting = "/admin/force-password-change-setting";

  // Company-wide Date Format setting (Settings → Date/Time Configurations)
  static getDateFormatSetting = "/admin/date-format-setting";
  static updateDateFormatSetting = "/admin/date-format-setting";

  //appraisals
  static addAppraisalMeetings = "/appraisal/addeditMeetings";

  //feedback
  static feedbackApi = "/feedback/emp_feedback";
  static postFeedbackData = "/feedback/saveempdataforfeedback";

  //icon & logo
  static getAdminSettings = "/adminsettings/adminSetting";

  //empleave
  static getempleave = "/empInOutTime/getLeaveOnCalender";

  // review forms (3 month / 6 month / 1 year milestone review)
  static reviewFormMyForms = "/reviewForm/myForms";
  static reviewFormDownload = "/reviewForm/download";
  static reviewFormUpload = "/reviewForm/upload";
  static reviewFormDeleteSubmission = "/reviewForm/deleteSubmission";
  static reviewFormSubmission = "/reviewForm/submission";
  static reviewFormPendingApprovals = "/reviewForm/pendingApprovals";
  static reviewFormApprove = "/reviewForm/approve";
  static reviewFormReject = "/reviewForm/reject";
  static reviewFormTeamForms = "/reviewForm/teamForms";
  static reviewFormSettings = "/reviewForm/settings";
  static reviewFormList = "/reviewForm/list";
  static reviewFormEmployeeSchedule = "/reviewForm/employee";
  static reviewFormWaive = "/reviewForm/waive";
  static reviewFormUnlockReplace = "/reviewForm/unlockReplace";
  static reviewFormReopen = "/reviewForm/reopen";
  static reviewFormReresolveTemplate = "/reviewForm/reresolveTemplate";
  static reviewFormRunAssignment = "/reviewForm/runAssignment";
  static reviewFormPreviewTemplate = "/reviewForm/previewTemplate";
  static reviewFormCountMatchingEmployees = "/reviewForm/countMatchingEmployees";

  //department module
  static departmentDropdownList = "/department/departmentDropdownList";
  static empdesignationDropdownList =
    "/empdepartment/empdesignationDropdownList";
  static empDepartmentList = "/empdepartment/empdepartmentList";
  static empDepartmentAdd = "/empdepartment/empaddDepartment";
  static empDeleteDepartment = "/empdepartment/empdeletDepartment";
  static empEditDepartment = "/empdepartment/empeditDepartment";
  static empUpdateApplicable = "/empdepartment/empUpdateApplicable";
  static empGteApplicable = "/empdepartment/empGteApplicable";

  static AddDEptHead = "/audits/add/department/head";
  static getDepHead = "/audits/get/list/department/head";
  static deleteDepHead = "/audits/delete/department/head";

  //announcement module
  static addAnnouncement = "/announcement/create";
  static listAnnouncement = "/announcement/list";
  static dashbordAnnouncementList = "/announcement/dashbordlist";
  static getAnnouncementById = "/announcement";
  static editAnnouncement = "/announcement/update";
  static deleteAnnouncement = "/announcement/delete";
  static toggleAnnouncementActive = "/announcement/toggle-active";
  static deleteAnnouncementImage = "/announcement/delete-image";
  /** Engage — POST /announcement/ack-report/:id, body { pageNum, pageLimit, search, type: "acked"|"pending" } */
  static announcementAckReportUrl = "/announcement/ack-report";
  /** Engage — POST /announcement/ack/:id, employee acknowledges an announcement */
  static announcementAck = "/announcement/ack";

  // Engage — Badges
  static engageBadgeCreate = "/engage/badge/create";
  static engageBadgeList = "/engage/badge/list";
  static engageBadgeUpdate = "/engage/badge/update";
  static engageBadgeToggle = "/engage/badge/toggle";
  static engageBadgeDelete = "/engage/badge/delete";
  static engageBadgeDropdown = "/engage/badge/dropdown";

  // Engage — Awards & certificates
  static engageAwardList = "/engage/award/list";
  static engageAwardDropdown = "/engage/award/dropdown";
  static engageAwardCreate = "/engage/award/create";
  static engageAwardUpdate = "/engage/award/update";
  static engageAwardToggle = "/engage/award/toggle";
  static engageAwardDelete = "/engage/award/delete";
  static engageAwardIssue = "/engage/award/issue";
  static engageAwardGrants = "/engage/award/grants";
  static engageAwardGrantById = "/engage/award/grant";

  // Engage — Wall (Post + Praise; Poll intentionally not built)
  static engageWallCreate = "/engage/wall/create";
  static engageWallList = "/engage/wall/list";
  static engageWallLike = "/engage/wall/like";
  static engageWallComment = "/engage/wall/comment";
  static engageWallComments = "/engage/wall/comments";
  static engageWallDelete = "/engage/wall/delete";
  static engageWallCertificateDownload = "/engage/wall/certificate";
  static engageWallUploadImage = "/engage/wall/upload-image";

  // Engage — Moderation (queue over Wall praise posts)
  static engageModerationList = "/engage/moderation/list";
  static engageModerationHide = "/engage/moderation/hide";
  static engageModerationRestore = "/engage/moderation/restore";
  static engageModerationDelete = "/engage/moderation/delete";

  // Engage — Settings (per-company singleton)
  static engageSettingsGet = "/engage/settings";
  static engageSettingsUpdate = "/engage/settings/update";
  static engageSettingsPause = "/engage/settings/pause";
  static engageSettingsResume = "/engage/settings/resume";

  // Engage — Overview (recognition analytics, read only)
  static engageOverview = "/engage/overview";

  // Announcement Category
  static announcementCategoryAdd = "/announcement/category/create";
  static announcementCategoryList = "/announcement/category/list";
  static announcementCategoryEdit = "/announcement/category/update";
  static announcementCategoryDelete = "/announcement/category/delete";
  static announcementCategoryDropdown = "/announcement/category/dropdown";
  static getAnnouncementCategoryById = "/announcement/category";
  static editDepHead = "/audits/update/department/head";

  static empSubDepartmentDropdownList =
    "/subDepartment/subDepartmentDropdownList";
  static empDesignationList = "/empdesignation/empdesignationList";
  static empDesignationAdd = "/empdesignation/empaddDesignation";
  static empDeleteDesignation = "/empdesignation/empdeleteDesignation";
  static empEditDesignation = "/empdesignation/empeditDesignation";

  static empaddShift = "/empshift/empaddShift";
  static empshiftList = "/empshift/empshiftList";
  static empdeleteShift = "/empshift/empdeleteShift";
  static empShiftById = "/empshift/empShiftById";
  static empeditShift = "/empshift/empeditShift";
  static empduplicateShift = "/empshift/empduplicateShift";
  static empimportShiftsSample = "/empshift/empimportShiftsSample";
  static empimportShifts = "/empshift/empimportShifts";
  static editEmpShiftGroup = "/emp/editEmpShiftGroup";

  // Shift Groups (Auto Shift) - one per Branch x Department, owns many shifts
  static shiftGroupList = "/shift-group/list";
  static shiftGroupAll = "/shift-group/all";
  static shiftGroupAdd = "/shift-group/add";
  static shiftGroupEdit = "/shift-group/edit";
  static shiftGroupDelete = "/shift-group/delete";
  static shiftGroupPreview = "/shift-group/preview";
  static shiftGroupExceptions = "/shift-group/exceptions";
  static shiftGroupImpact = "/shift-group/impact";
  static shiftGroupImportSample = "/shift-group/import-sample";
  static shiftGroupImport = "/shift-group/import";
  static editEmpProductAccess = "/emp/editEmpProductAccess";

  //add admin
  static adminSetting = "/adminsettings/adminSetting";

  //WFH module

  static addwfh = "/wfh_request/addWFH";
  static wfhlisting = "/wfh_request/WFHList";
  static wfhapprove = "/wfh_request/ApproveWFHApplication";
  static wfhreject = "/wfh_request/RejectWFHApplication";
  static adminwfhapprove = "/wfh_request/ApproveWFHApplicationadmin";
  static adminwfhreject = "/wfh_request/RejectWFHApplicationadmin";

  // employee master
  static addemp = "/emp/addemp";
  static empmasterdepartment = "/empdepartment/empdepartmentDropdownList";
  static empmasterdesignation = "/empdepartment/empdesignationDropdownList";
  static getempmaster = "/emp/getempbyid";
  static editempmaster = "/emp/editemp";
  static emplist = "/emp/employeesList";
  static empDirectoryIds = "/emp/employeeDirectoryIds";
  static columnPreference = "/column-preference";
  static internlist = "/emp/internsList";
  static empdelete = "/emp/deleteEmp";
  static sendwelcomemail = "/emp/welcomeemail";
  static sendwelcomenoteemail = "/emp/welcomenoteemail";
  static deleteEmpimgandsign = "/emp/deleteEmpimgandsign";
  static sendfeedbackemail = "/emp/sendfeedbackemailcron";

  static empAddress = "/emp/address";
  static empPersonalInfo = "/emp/personal_info";
  static empBankDetails = "/emp/bank_details";
  static ifscCheck = "/emp/ifsc";
  static candidateIfscCheck = "/onboardingEmp/ifsc";
  static additionalInfoUrl = "/emp/addition_details";
  // Employment Details / Payroll Details sections of the Employee Master form
  static employeePayrollOptions = "/emp/payroll-options";
  static employeePayrollDetails = "/emp/payroll-details";
  static employeeLeavePolicy = "/emp/leave-policy";
  static contactList = "/emp/get/contacts";
  static addContact = "/emp/contacts";
  static deleteContact = "/emp/contacts/delete";

  //Admin In-out
  static empinout = "/empInOutTime/getEmpInoutTimeAdmin";
  static empinoutV2 = "/empInOutTime/getEmpInoutTimeAdminV2";
  static empinoutCompanyStatsV2 = "/empInOutTime/getEmpInoutCompanyStatsAdminV2";
  static deletebulkinout = "/empInOutTime/deleteEmpInOut";
  static deletinout = "/empInOutTime/deleteEmpInOutTime";
  static viewmuliplepunch = "/empInOutTime/viewmultiplepunch";
  static empdropdownlistbyreportingmanager =
    "/emp/empdropdownlistbyreportingmanager";
  static empdropdownlistbysubreportingmanager =
    "/emp/empdropdownlistbysubreportingmanager";
  static editinout = "/empInOutTime/editEmpInOut";
  static exportinoutcsv = "/empInOutTime/exportCsvEmpInoutTimeAdmin";
  static manualinout = "/empInOutTime/manually/inout";
  static seedAttendance = "/empInOutTime/seed-attendance";

  static addTeamRanking = "/UserRanking/calculate-rating";

  static empListRankingbySubReportingManager =
    "/emp/empdropdownlistbysubreportingmanagerRanking";
  static empListRankingbyReportingManager =
    "/emp/empdropdownlistbyreportingmanagerRanking";
  static empListRanking = "/emp/empdropdownlistRanking";

  // Admin Leave Module

  static leavemaster = "/leave_master/LeaveList/";
  static LeaveListmaster = "/leave_master/LeaveListmaster/";

  static addleavemaster = "/leave_master/addLeave/";
  static adminleavelist = "/leave_master/EmployessLeaveList";
  static leaveapproval = "/leave_master/ApproveLeaveApplication";
  static leavereject = "/leave_master/RejectLeaveApplication";
  static adminleavebalance = "/leave_master/EmpLeaveList";
  static adminaddleave = "/leave_master/addLeaveApplication";
  static getreportingmanager = "/emp/getreportingmanagerbyempid";
  static adminleavecancellation = "/leave_master/EmployessLeaveCancelationList";
  static admincancellationapprove =
    "/leave_master/ApproveCancelLeaveApplication";
  static admincancellationreject = "/leave_master/RejectCancelLeaveApplication";
  static compoffleaveapprove = "/leave_master/ApprovecompoffApplication";
  static compoffleavereject = "/leave_master/RejectcompoffApplication";
  static getEncashableLeaves = "/leave_master/getEncashableLeaves";
  static editleavebalance = "/leave_master/EditEmpLeave";
  static updateLeaveMaster = "/leave_master/updateLeaveMaster";

  static ApproveCancelLeaveApplicationadmin =
    "/leave_master/ApproveCancelLeaveApplicationadmin";
  static RejectCancelLeaveApplicationadmin =
    "/leave_master/RejectCancelLeaveApplicationadmin";
  static ApproveLeaveApplicationadmin =
    "/leave_master/ApproveLeaveApplicationadmin";
  static RejectLeaveApplicationadmin =
    "/leave_master/RejectLeaveApplicationadmin";
  static TLCompoffApplicationList = "/leave_master/TLCompoffApplicationList";
  static DirectorCompoffApplicationList =
    "/leave_master/DirectorCompoffApplicationList";
  static CEOCompoffApplicationList = "/leave_master/CEOCompoffApplicationList";

  static TLCompoffapproval = "/leave_master/TLApprovecompoffApplication";
  static TLCompoffreject = "/leave_master/TLRejectcompoffApplication";
  static CeoCompoffapproval = "/leave_master/CEOApprovecompoffApplication";
  static CeoCompoffreject = "/leave_master/CEORejectcompoffApplication";
  static DirectorCompoffapproval =
    "/leave_master/DirectorApprovecompoffApplication";
  static DirectorCompoffreject =
    "/leave_master/DirectorRejectcompoffApplication";
  static TLExportCompoffApplicationList =
    "/leave_master/TLExportCompoffApplicationList";
  static CEOExportCompoffApplicationList =
    "/leave_master/CEOExportCompoffApplicationList";
  static DirectorExportCompoffApplicationList =
    "/leave_master/DirectorExportCompoffApplicationList";
  static DirectorExportCompoffApplicationList =
    "/leave_master/DirectorExportCompoffApplicationList";
  static ExportCompoffApplicationList =
    "/leave_master/ExportCompoffApplicationList";
  static exportEmpLeaveList = "/leave_master/exportEmpLeaveList";
  static importEmpLeaveList = "/importFile/emp/leaves";
  // static importEmpLeaveApplications = "/importFile/emp/leave-applications";
  // Sample CSV / import templates pre-filled with the company's real employees
  static leaveBalanceTemplate = "/importFile/emp/leaves/template";
  static leaveApplicationTemplate = "/importFile/emp/leave-applications/template";
  static attendanceTemplate = "/empInOutTime/attendance-template";
  static bulkUpdateTemplate = "/emp/bulkUpdateEmployees/template";
  static rosterTemplate = "/roster/import-template";
  static assignLeaveRulesTemplate = "/leave-settings-v2/assignments/import-template";
  static workweekAssignTemplate = "/workweek/assign/template";
  static workweekAssignExport = "/workweek/assign/export";
  static workweekAssignImport = "/workweek/assign/import";
  static leaveImportUpload = "/importFile/upload";
  static leaveImportHistory = "/importFile/history";
  static leaveImportStatus = "/importFile/status";
  static leaveImportUndo = "/importFile/undo";
  static leaveImportDownloadErrors = "/importFile/download-errors";
  static leaveImportStatistics = "/importFile/statistics";
  static leaveImportCancel = "/importFile/cancel";

  //Admin-emp-Regularization - closed
  static adminempreg = "/employeeReg/getRegularisationRequest";
  static approvereg = "/employeeReg/acceptregularisation";
  static rejectreg = "/employeeReg/rejectregularisation";

  // Attendance Regularization bulk import (CSV/Excel) - endpoint paths follow
  // the same "/<module>-import/<action>" convention as attendance-import
  // (see server/routes/attendanceImport.js). NOT YET CONFIRMED against the
  // server - the backend agent building these routes had not landed them in
  // server/routes/ as of this writing. Update these 8 constants (and
  // regularizationImportTemplate below) once server/routes/index.js actually
  // mounts the module - grep server/routes for "regularization-import".
  static regularizationImportUpload = "/regularization-import/upload";
  static regularizationImportStatus = "/regularization-import/status";
  static regularizationImportHistory = "/regularization-import/history";
  static regularizationImportDownloadErrors = "/regularization-import/download-errors";
  static regularizationImportUndo = "/regularization-import/undo";
  static regularizationImportStatistics = "/regularization-import/statistics";
  static regularizationImportCancel = "/regularization-import/cancel";
  static regularizationImportTemplate = "/regularization-import/template";

  // Separation/Resignation, Expense/Reimbursement and Shift Swap/Change bulk
  // import (CSV/Excel) - same "/<module>-import/<action>" convention as
  // attendance-import / regularization-import above. NOT YET CONFIRMED
  // against the server - a backend agent was building these three modules'
  // routes in parallel and had not landed them in server/routes/ as of this
  // writing (grepped server/routes for "separation-import", "expense-import",
  // "shiftchange-import" - no matches yet). Update these 24 constants once
  // server/routes/index.js actually mounts each module.
  static separationImportUpload = "/separation-import/upload";
  static separationImportStatus = "/separation-import/status";
  static separationImportHistory = "/separation-import/history";
  static separationImportDownloadErrors = "/separation-import/download-errors";
  static separationImportUndo = "/separation-import/undo";
  static separationImportStatistics = "/separation-import/statistics";
  static separationImportCancel = "/separation-import/cancel";
  static separationImportTemplate = "/separation-import/template";

  static expenseImportUpload = "/expense-import/upload";
  static expenseImportStatus = "/expense-import/status";
  static expenseImportHistory = "/expense-import/history";
  static expenseImportDownloadErrors = "/expense-import/download-errors";
  static expenseImportUndo = "/expense-import/undo";
  static expenseImportStatistics = "/expense-import/statistics";
  static expenseImportCancel = "/expense-import/cancel";
  static expenseImportTemplate = "/expense-import/template";

  static shiftchangeImportUpload = "/shiftchange-import/upload";
  static shiftchangeImportStatus = "/shiftchange-import/status";
  static shiftchangeImportHistory = "/shiftchange-import/history";
  static shiftchangeImportDownloadErrors = "/shiftchange-import/download-errors";
  static shiftchangeImportUndo = "/shiftchange-import/undo";
  static shiftchangeImportStatistics = "/shiftchange-import/statistics";
  static shiftchangeImportCancel = "/shiftchange-import/cancel";
  static shiftchangeImportTemplate = "/shiftchange-import/template";

  // Admin Reports

  static attendenceregister = "/employeeReg/attendanceRegisterList";
  static attendenceregistercsv = "/employeeReg/exportAttendanceRegisterListCSV";
  static newattendenceregistercsv =
    "/employeeReg/newexportAttendanceRegisterListCSV";
  static getRegulatisationList = "/employeeReg/getRegulatisationList";
  static getRegulatisationReport = "/employeeReg/getRegulatisationReport";
  static getRegulatisationReportPDF = "/employeeReg/getRegulatisationReportPDF";
  static exportMonthlyRegisterListCSV =
    "/employeeReg/exportMonthlyRegisterListCSV";

  static exportattendenceregister = "/employeeReg/exportAttendanceRegisterList";
  static exportMonthlyRegisterList = "/employeeReg/exportMonthlyRegisterList";

  // V2 Revamped Report APIs
  static attendanceRegisterV2 = "/employeeReg/v2/attendanceRegisterData";
  static monthlyRegisterV2 = "/employeeReg/v2/monthlyRegisterData";
  static exportAttendanceRegisterV2 = "/employeeReg/v2/exportAttendanceRegister";
  static exportMonthlyRegisterV2 = "/employeeReg/v2/exportMonthlyRegister";
  static exportAttendanceRegisterV2Pdf = "/employeeReg/v2/exportAttendanceRegisterPdf";
  static exportMonthlyRegisterV2Pdf = "/employeeReg/v2/exportMonthlyRegisterPdf";

  // V3 Report APIs - shared attendance derivation (synced with the employee
  // Attendance Regularization page and dashboard Attendance Calendar)
  static attendanceRegisterV3 = "/employeeReg/v3/attendanceRegisterData";
  static monthlyRegisterV3 = "/employeeReg/v3/monthlyRegisterData";
  static exportAttendanceRegisterV3 = "/employeeReg/v3/exportAttendanceRegister";
  static exportMonthlyRegisterV3 = "/employeeReg/v3/exportMonthlyRegister";
  static exportAttendanceRegisterV3Pdf = "/employeeReg/v3/exportAttendanceRegisterPdf";
  static exportMonthlyRegisterV3Pdf = "/employeeReg/v3/exportMonthlyRegisterPdf";
  // "My Reports" variants: same V3 data, but the server pins the employee to the
  // logged-in user, so no employee/branch/department filter is sent or honoured.
  static userAttendanceRegisterV3 = "/employeeReg/v3/user/attendanceRegisterData";
  static userExportAttendanceRegisterV3 = "/employeeReg/v3/user/exportAttendanceRegister";
  static userExportAttendanceRegisterV3Pdf = "/employeeReg/v3/user/exportAttendanceRegisterPdf";
  static penaltyViolationReport = "/employeeReg/getPenaltyViolationReport";
  static appliedPenaltyList = "/employeeReg/getAppliedPenaltyList";
  static revokeAppliedPenalty = "/employeeReg/revokeAppliedPenalty";
  static penaltySummaryReport = "/employeeReg/getPenaltySummaryReport";
  static punchRejectionReport = "/employeeReg/getPunchRejectionReport";
  static shiftHistoryEmployees = "/employeeReg/getShiftHistoryEmployees";
  static employeeShiftHistory = "/employeeReg/getEmployeeShiftHistory";
  static reprocessAttendance = "/employeeReg/v2/reprocessAttendance";
  static leaveapplicationreport = "/employeeReg/leaveList";
  static exportleaveapplicationreport = "/employeeReg/exportLeaveList";
  static exportemployeeinout = "/employeeReg/exportEmployeeInOut";
  static exportemployeeinoutCSV = "/employeeReg/exportEmployeeInOutCSV";
  static exportInOutRegisterAdminCSV = "/employeeReg/exportInOutRegisterAdminCSV";

  static exportleaveapproval = "/employeeReg/exportLeaveApproval";
  static employeeleavebalance = "/employeeReg/LeaveBalanceList";
  static exportemployeeleavebalance = "/employeeReg/exportLeaveBalance";
  static joinemployeelist = "/emp/JoinemployeesList";
  static exportjoinemployeelist = "/emp/exportJoinemployeesList";
  static leftemployeereport = "/emp/LeftempList";
  static exportleftemployeereport = "/emp/exportLeftempList";

  // HRMS passwords

  static hrmsforgotpaassword = "/emp/forgotPassword";
  static hrmsresetpassword = "/emp/resetPassword";
  static hrmschangepassword = "/emp/updatePassword";

  // static addemp = "/emp/addemp";
  static empdepartmentDropdownList = "/empdepartment/empdepartmentDropdownList";
  static departmentDropdownList = "/department/departmentDropdownList";
  static exportEmployeeList = "/emp/exportemployeesList";
  static getExportColumnMapping = "/emp/getExportColumnMapping";
  static exportDetailedEmployeeList = "/emp/export/details";
  static exemplist = "/emp/LeftemployeesList";
  static editexemp = "/emp/editXemp/";
  static bulkEditReportingManager = "/emp/bulkEditReportingManager";
  static deleteexempdoc = "/emp/DeleteFile";
  static importEmployeeList = "/emp/bulkImportEmployees";
  static bulkUpdateEmployees = "/emp/bulkUpdateEmployees"

  static exportExEmployeeList = "/emp/exporLefttemployeesList";
  static add_Resign = "/emp/addresign";
  static empDropdownList = "/emp/empdropdownlist";
  static empDropdownListForLeaveBal = "/emp/empdropdownlistforleaveBal";
  static adminInOutempdropdownList = "/emp/adminInoutDropdown";

  static rejoinEmp = "/emp/rejoinEmp";

  // Employment Type
  static addEmploymentType = "/EmpType/addEmpType";
  static updateEmploymentType = "/EmpType/updateEmpType";
  static listEmploymentTypes = "/EmpType/empTypeList";
  static deleteEmploymentType = "/EmpType/deleteEmpType";
  static empTypeById = "/EmpType/empTypeById";
  static typeDropdownList = "/EmpType/empTypeDropdownList";
  //employee ranking system

  static calculaterank = "/UserRanking/calculate-rating";
  static addemprank = "/UserRanking/addUserRating";
  static empranklist = "/UserRanking/UserRatingList";
  static editemprank = "/UserRanking/editUserRating/";
  static deletemprank = "/UserRanking/deleteUserRating";
  static exportemprank = "/employeeReg/exportUserRanking";
  static deletmultiemprank = "/UserRanking/deleteMultipleUserRating";
  static editemprankmaster =
    "/performance-attribute-category/editPerformanceCategory/";
  static emprankchart = "/UserRanking/userRatingchart";
  static exportuserRatingListing = "/UserRanking/exportuserRatingListing";

  static departmentByReportingManager = "/emp/departmentByReportingManager";

  //event list
  static getAdminEventViewById = "/admin/event/getAdminEventViewById";
  static eventAdd = "/event/addEvent";
  static eventById = "/event/eventById";
  static editEvent = "/event/editEvent";
  static saveEventNotification = "/event/saveEventNotification";
  static eventDelete = "/event/deleteEvent";
  static eventTypeDropdownList = "/eventType/eventTypeDropdownList";
  static eventClone = "/event/eventClone";
  static eventStatus = "/event/eventStatus";
  static getEventForm = "/elevateForms/getEventForm";
  static feedbackById = "/feedback/feedbackById";
  static saveEventForm = "/elevateForms/saveEventForm";
  static exportfeedbackList = "/feedback/exportfeedbackList";
  static Averagefeedback = "/feedback/Averagefeedback";

  static addsurvey = "/surveys/addsurvey";
  static editsurvey = "/surveys/editSurvey";
  static surveyList = "/surveys/surveyList";
  static surveyById = "/surveys/surveyById";
  static SurveyUsersList = "/surveys/SurveyUsersList";
  static surveyAnswersById = "/surveys/surveyAnswersById";

  //  user In-out
  static addintime = "/empInOutTime/addInTime";
  static addoutTime = "/empInOutTime/addOutTime";
  static getemprecord = "/empInOutTime/getEmpInoutTime";
  static empimportcsv = "/empInOutTime/importcsv";
  static attendanceImportUpload = "/attendance-import/upload";
  static attendanceImportStatus = "/attendance-import/status";
  static attendanceImportHistory = "/attendance-import/history";
  static attendanceImportDownloadErrors = "/attendance-import/download-errors";
  static attendanceImportCancel = "/attendance-import/cancel";
  // NOTE(2026-09-25): a backend agent was building the matching wfh-import /
  // onduty-import APIs in parallel with this client work. These paths mirror
  // the established "/attendance-import/*" convention (upload, status/:id,
  // history, download-errors/:id, cancel/:id) plus undo/:id + statistics
  // (matching the leaveImport* convention below). Verify these against the
  // actual mounted server routes (server/routes/index.js) before relying on
  // them - at the time this file was written, server/routes had no
  // wfh-import or onduty-import route file yet.
  static wfhImportUpload = "/wfh-import/upload";
  static wfhImportStatus = "/wfh-import/status";
  static wfhImportHistory = "/wfh-import/history";
  static wfhImportDownloadErrors = "/wfh-import/download-errors";
  static wfhImportUndo = "/wfh-import/undo";
  static wfhImportStatistics = "/wfh-import/statistics";
  static wfhImportCancel = "/wfh-import/cancel";
  static wfhImportTemplate = "/wfh-import/template";
  static ondutyImportUpload = "/onduty-import/upload";
  static ondutyImportStatus = "/onduty-import/status";
  static ondutyImportHistory = "/onduty-import/history";
  static ondutyImportDownloadErrors = "/onduty-import/download-errors";
  static ondutyImportUndo = "/onduty-import/undo";
  static ondutyImportStatistics = "/onduty-import/statistics";
  static ondutyImportCancel = "/onduty-import/cancel";
  static ondutyImportTemplate = "/onduty-import/template";
  static exportInOutList = "/empInOutTime/exportAttendanceCsv";
  static punchStatus = "/empInOutTime/getPunchStatus";
  static punchInOut = "/empInOutTime/punchInOut";

  // user attendence Regularization
  static getemployeeAttendence = "/employeeReg/getEmployeeAttendance";
  static getemployeeAttendenceV2 = "/employeeReg/getEmployeeAttendanceV2";
  static addRegularization = "/employeeReg/reqRegularisation";
  static missPunchEligibility = "/employeeReg/missPunch/eligibility";
  static missPunchApprovalDetail = "/employeeReg/missPunch/approvalDetail";
  static getAttendanceSettings = "/employeeReg/getAttendanceSettings";
  static updateAttendanceSettings = "/employeeReg/updateAttendanceSettings";

  // Time calculation mode (Hours/Minutes) — a set-once value per company, no
  // effective-date/history concept. `getAttendanceSettings` above already
  // returns the mode currently in effect (`timeCalculationMode`) — these two
  // cover setting it once and reading the single configured row (if any).
  static timeCalculationModeSet = "/time-calculation-mode/set";
  static timeCalculationModeGet = "/time-calculation-mode";

  // notice period policies — rule-based notice period (docs/notice-period)
  static noticePeriodPolicies = "/notice-period/policies";
  static noticePeriodPolicyReorder = "/notice-period/policies/reorder";
  static noticePeriodPolicyPreview = "/notice-period/policies/preview";
  static noticePeriodPolicyMatchCount = "/notice-period/policies/match-count";
  static noticePeriodResolve = "/notice-period/resolve";

  // WFH policies - rule-based work-from-home allowance (General Settings > WFH Setting)
  static listWfhPolicies = "/wfh-policy/policies";
  static createWfhPolicy = "/wfh-policy/policies";
  static updateWfhPolicy = "/wfh-policy/policies/";
  static deleteWfhPolicy = "/wfh-policy/policies/";
  static reorderWfhPolicies = "/wfh-policy/policies/reorder";
  static resolveWfhPolicy = "/wfh-policy/resolve";

  //  user leave module
  static addLeaveApplication = "/leave_master/addLeaveApplication";
  static getSandwichPolicy = "/leave_master/sandwich-policy";
  static updateSandwichPolicy = "/leave_master/sandwich-policy";
  static calculateSandwichDays = "/leave_master/calculate-sandwich-days";
  static employeeLeaveList = "/leave_master/EmployessLeaveList";
  static EmpLeaveListleaveBal = "/leave_master/EmpLeaveListleaveBal";
  static empLeaveBalance = "/leave_master/EmpLeaveList";
  static leavecancellationbyId = "/leave_master/getleaveapplicationbyid";
  static reportingmanager = "/emp/getreportingmanagerbyempid";
  static addleavecancellation = "/leave_master/AddCancelLeaveApplication";
  static empCompOffLeaveBalance = "/leave_master/CompoffLeaveList";
  static addCompOffLeave = "/leave_master/AddcompoffApplication";
  static cOffLeaveList = "/leave_master/CompoffApplicationList";
  static encashment = "/leave_master/EncashmentcompoffApplication";

  // Comp-off bulk import (CSV/Excel) - same "/<module>-import/<action>"
  // convention as attendance-import/regularization-import above. NOT YET
  // CONFIRMED against the server - grep server/routes for "compoff-import"
  // once the backend agent building these routes has landed them, and update
  // these 8 constants if the mounted path differs.
  static compoffImportUpload = "/compoff-import/upload";
  static compoffImportStatus = "/compoff-import/status";
  static compoffImportHistory = "/compoff-import/history";
  static compoffImportDownloadErrors = "/compoff-import/download-errors";
  static compoffImportUndo = "/compoff-import/undo";
  static compoffImportStatistics = "/compoff-import/statistics";
  static compoffImportCancel = "/compoff-import/cancel";
  static compoffImportTemplate = "/compoff-import/template";

  // Flexi Leave - a short, company-configurable block of hours off on a
  // single day (arriving late / leaving early / a plain duration for
  // working-hours-only shifts). See server/routes/hrms_module/flexiLeave.js.
  static flexiLeaveAdd = "/flexi-leave/add";
  static flexiLeaveMy = "/flexi-leave/my";
  static flexiLeaveTeam = "/flexi-leave/team";
  static flexiLeaveAdminList = "/flexi-leave/admin/list";
  static flexiLeaveCancel = "/flexi-leave/cancel";
  static flexiLeaveApprove = "/flexi-leave/approve";
  static flexiLeaveReject = "/flexi-leave/reject";
  static flexiLeaveAdminApprove = "/flexi-leave/admin/approve";
  static flexiLeaveAdminReject = "/flexi-leave/admin/reject";
  static flexiLeaveSettings = "/flexi-leave/settings";
  static flexiLeaveUsage = "/flexi-leave/usage";
  static flexiLeaveImportTemplate = "/flexi-leave/import/template";
  static flexiLeaveImportUpload = "/flexi-leave/import/upload";
  static flexiLeaveImportHistory = "/flexi-leave/import/history";
  static flexiLeaveImportStatus = "/flexi-leave/import/status";
  static flexiLeaveImportDownloadErrors = "/flexi-leave/import/download-errors";
  static flexiLeaveImportUndo = "/flexi-leave/import/undo";
  static flexiLeaveImportCancel = "/flexi-leave/import/cancel";

  /**
   * Submits a new Flexi Leave request for the logged-in employee.
   * @param {{application_date: string, flexi_type?: ("LATE_IN"|"EARLY_OUT"|null), duration_minutes: number, reason?: string}} payload
   */
  static addFlexiLeaveApplication = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.flexiLeaveAdd,
      body: payload,
    });
  };

  /** Paginated list of the logged-in employee's own Flexi Leave requests. */
  static getMyFlexiLeaveApplications = async ({ page = 1, limit = 10 } = {}) => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.flexiLeaveMy,
      body: { page, limit },
    });
  };

  /**
   * Paginated, searchable/filterable list of the caller's reportees' Flexi
   * Leave requests - Reporting Manager / Assistance Reporting Manager only.
   * Each row already carries a bundled `approval_progress` field (server-side
   * `getProgressMap` call), so no per-row follow-up fetch is needed on the
   * client. POST (not GET) - richer Joi-validated filter body, mirroring
   * `wfhlisting`'s own convention (see `GetTeamFlexiLeaveApplications` in
   * server/controller/hrms_module/flexiLeaveApplicaion.js).
   * @param {{pageNum?: number, pageLimit?: number, search?: string, application_from_date?: string, application_to_date?: string, approval_from_date?: string, approval_to_date?: string, status?: string, branch?: string, employee_id?: string, emp_code?: string}} body
   */
  static getTeamFlexiLeaveApplications = async (body = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.flexiLeaveTeam,
      body,
    });
  };

  /**
   * Paginated, searchable/filterable, company-wide list of Flexi Leave
   * requests - Admin/HR only. POST (not GET) - same body shape as
   * `getTeamFlexiLeaveApplications`, plus `reporting_manager_id`/
   * `filter_reporting_manager_id` (see `GetAdminFlexiLeaveApplications`).
   * @param {Object} body
   */
  static getAdminFlexiLeaveApplications = async (body = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.flexiLeaveAdminList,
      body,
    });
  };

  /** Applicant cancels their own still-PENDING Flexi Leave request. */
  static cancelFlexiLeaveApplication = async (id, payload = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.flexiLeaveCancel}/${id}`,
      body: payload,
    });
  };

  /** Reporting Manager / Assistance Reporting Manager decision. */
  static approveFlexiLeaveApplication = async (id, payload = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.flexiLeaveApprove}/${id}`,
      body: payload,
    });
  };

  static rejectFlexiLeaveApplication = async (id, payload = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.flexiLeaveReject}/${id}`,
      body: payload,
    });
  };

  /** Admin/HR override decision - bypasses RM/ARM reportee-scoping. */
  static adminApproveFlexiLeaveApplication = async (id, payload = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.flexiLeaveAdminApprove}/${id}`,
      body: payload,
    });
  };

  static adminRejectFlexiLeaveApplication = async (id, payload = {}) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.flexiLeaveAdminReject}/${id}`,
      body: payload,
    });
  };

  /**
   * Reads `companies.flexi_leave_setting`, filled in with the schema's own
   * defaults for any key that predates this field. Any authenticated company
   * user may call this - the Apply form itself needs it.
   */
  static getFlexiLeaveSettings = async () => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.flexiLeaveSettings,
    });
  };

  /**
   * Reads the logged-in employee's current Flexi Leave usage cycle snapshot
   * (`{ limit, used, remaining, cycle }`) - `limit`/`remaining` are `null`
   * when the company has no usage cap configured. Used by the Apply form to
   * show a plain "X remaining" readout above Type/Duration.
   */
  static getFlexiLeaveUsage = async (params) => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.flexiLeaveUsage,
      params,
    });
  };

  /** Admin/HR only - updates `companies.flexi_leave_setting` as a whole. */
  static updateFlexiLeaveSettings = async (payload) => {
    return this.makeAPICall({
      methodName: this.putMethod,
      api_url: this.flexiLeaveSettings,
      body: payload,
    });
  };

  // Flexi Leave rules - rule-based replacement for the old flat
  // flexi_leave_setting's interval/max-duration/usage-limit/applicable-
  // employees fields (General Settings > Flexi Leave Setting). Mirrors
  // WFH policies' evaluation-order/first-match-wins shape, but matches
  // AND-across-dimensions, not WFH's OR (see flexiLeaveRule.js controller).
  static flexiLeaveRules = "/flexi-leave/rules";
  static flexiLeaveRuleReorder = "/flexi-leave/rules/reorder";
  static flexiLeaveRuleResolve = "/flexi-leave/rules/resolve";

  /** Paginated, evaluation-ordered list of Flexi Leave rules, criteria hydrated with display names. */
  static getFlexiLeaveRules = async ({ page = 1, limit = 10, includeInactive = true } = {}) => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.flexiLeaveRules,
      body: { page, limit, includeInactive },
    });
  };

  /** Admin/HR only - creates a rule, appended at the bottom of the evaluation order. */
  static createFlexiLeaveRule = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.flexiLeaveRules,
      body: payload,
    });
  };

  /** Admin/HR only - full replace of a rule's fields. */
  static updateFlexiLeaveRule = async (id, payload) => {
    return this.makeAPICall({
      methodName: this.putMethod,
      api_url: `${this.flexiLeaveRules}/${id}`,
      body: payload,
    });
  };

  /** Admin/HR only - soft deletes a rule and closes the gap in the evaluation order. */
  static deleteFlexiLeaveRule = async (id) => {
    return this.makeAPICall({
      methodName: this.deleteMethod,
      api_url: `${this.flexiLeaveRules}/${id}`,
    });
  };

  /** Admin/HR only - rewrites the whole evaluation order ({ order: [id, ...] }). */
  static reorderFlexiLeaveRules = async (order) => {
    return this.makeAPICall({
      methodName: this.patchMethod,
      api_url: this.flexiLeaveRuleReorder,
      body: { order },
    });
  };

  /**
   * "Test an employee" preview - resolves the rule (if any) winning for one
   * employee, plus every active rule that would have matched. Mirrors
   * `noticePeriodPolicyPreview`'s shape, minus tenure/company-default
   * (see flexiLeaveRule.js's ResolveFlexiLeaveRule).
   */
  static resolveFlexiLeaveRule = async (employeeId) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.flexiLeaveRuleResolve,
      body: { employee_id: employeeId },
    });
  };

  // Overtime - employee-submitted extra-hours requests ({date, hours, reason},
  // hours as an "HH:mm" string). Approval is PARTIAL, not binary accept/reject:
  // the approver can sign off on anywhere from "00:00" up to the applied
  // `hours`, never more. See server/routes/overtime.js.
  static overtimeBase = "/overtime";
  static overtimeUpdate = "/overtime/update";
  static overtimeStatus = "/overtime/status";
  static overtimeDelete = "/overtime/delete";
  static overtimeSettings = "/overtime/settings";
  static overtimeEligibleDays = "/overtime/eligible-days";

  /** @param {{date: string, reason?: string}} payload - hours is sourced server-side from the real computed attendance figure for that date */
  static addOvertimeApplication = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.overtimeBase,
      body: payload,
    });
  };

  /**
   * Days the caller is actually eligible to apply Overtime for - each entry
   * is `{date, hours}` where `hours` ("HH:mm") is the real figure the
   * attendance system already computed for that date. Backs the Apply
   * form's date dropdown so an employee can no longer free-pick a date or
   * type their own hours.
   */
  static getOvertimeEligibleDays = async () => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.overtimeEligibleDays,
    });
  };

  /**
   * Paginated, filterable list - the SAME endpoint serves the employee's own
   * requests, a Reporting Manager/Assistance Reporting Manager's team, and
   * Admin/HR's company-wide view; the server scopes the result set by the
   * caller's role.
   * @param {{page?: number, limit?: number, status?: string, date_from?: string, date_to?: string, employee_id?: string, reporting_manager_id?: string, search?: string}} params
   */
  static getOvertimeApplications = async (params = {}) => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.overtimeBase,
      body: params,
    });
  };

  /** Edits a still-PENDING request. */
  static updateOvertimeApplication = async (id, payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.overtimeUpdate}/${id}`,
      body: payload,
    });
  };

  /**
   * Approves or rejects exactly ONE request. `approved_hours` ("HH:mm") is
   * required when approving and must sit between "00:00" and the request's
   * own applied `hours` - this is the partial-approval behaviour that
   * replaced the old bulk Accept/Reject.
   * @param {{id: string, status: ("approved"|"rejected"), approved_hours?: string}} payload
   */
  static updateOvertimeStatus = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.overtimeStatus,
      body: payload,
    });
  };

  /** Soft deletes a still-PENDING request. */
  static deleteOvertimeApplication = async (id) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.overtimeDelete}/${id}`,
    });
  };

  /** Company-wide "Overtime Approval Required" toggle - any authenticated company user may read it (the Apply screen needs it to gate its own entry point). */
  static getOvertimeSettings = async () => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.overtimeSettings,
    });
  };

  /** Admin only - updates the toggle. */
  static updateOvertimeSettings = async (payload) => {
    return this.makeAPICall({
      methodName: this.putMethod,
      api_url: this.overtimeSettings,
      body: payload,
    });
  };

  // user dashboard
  static userdashboard = "/emp/userdashboard";
  static getholidays = "/holidays/holidayList";
  static adminholidays = "/holidays/adminholidaylist";

  static addholidays = "/holidays/addHoliday";
  static deleteholidays = "/holidays/deleteHoliday";
  static editholidays = "/holidays/holidayById";
  static updateholidays = "/holidays/editHoliday";
  static importHolidays = "/holidays/importHolidays";
  static getHolidaySetting = "/holidays/getHolidaySetting";
  static updateHolidaySetting = "/holidays/updateHolidaySetting";

  //Team leader
  static getteamattandance = "/empInOutTime/getReportingTodayInOut";

  // Admin dashboard
  static admindashboard = "/emp/admindashboard";
  static getempinout = "/empInOutTime/getTodayInOut";
  static multiplepunches = "/empInOutTime/getmultipalpunchByEmpID";
  static addManualPunch = "/empInOutTime/addmanualpunch";
  static editManualPunch = "/empInOutTime/editmanualpunch";
  static deleteManualPunch = "/empInOutTime/deletemanualpunch";

  //Organization policy

  static organizationpolicy = "/organization-policies/PDFList";
  static deletePDF = "/organization-policies/deletePolicy";
  static addPDF = "/organization-policies/addPDF";
  static editPDF = "/organization-policies/editPDF";
  static DeletePolicyFile = "/organization-policies/DeletePolicyFile";
  static acknowledgePolicy = "/organization-policies/acknowledgePolicy";
  static policyAcknowledgementList =
    "/organization-policies/acknowledgementList";

  //trainer module
  static hostlist = "/host/hostlist";
  static addTrainner = "/host/addhost";
  static hostDropdownList = "/host/hostDropdownList";
  static hostMyEvents = "/host/hostMyEvents";
  static hostAttendanceReport = "/host/hostAttendanceReport";

  //Upload Employee Documents
  static uploadEmployeeDoc = "/employeedoc/uploadDoc";
  static uploadEmployeeDocSign = "/employeedoc/uploadEmpSign";
  static getDocByEmpId = "/employeedoc/getDocbyEmpId";
  static getSignedDocumentsByEmpId = "/employeedoc/getSignedDocumentsByEmpId";
  static downloadSignedDocument = "/employeedoc/downloadSignedDocument";
  static deleteDocbyEmpId = "/employeedoc/deleteDocbyEmpId";
  static downloadDoc = "/employeedoc/download";
  static downloadDocDirect = "/employeedoc/downloadDocDirect";
  // static downloadDocument = "/employeedoc/downloadDocument";
  static downloadDocument = "/employeedoc/downloadDocForAmbit";

  // separation module
  static addresign = "/separationNew/separation";
  static exitForm = "/separationNew/exitForm";
  static getresign = "/separationNew/getseparation";
  static editresign = "/separationNew/editseparation";
  static last_working = "/separationNew/getseparationLWDlist";
  static userResignList = "/separationNew/getmySeparationData";
  static separationByAdminForUser = "/separationNew/separationbyAdmin";

  // send exit mails
  static sendExitMails = "/emp/sendfeedbackemailcron";
  static sendExitMailsToSelected = "/emp/send-exit-formalities-email";
  static sendWarningEmail = "/emp/send-warning-email";
  static sendPasswordChangeMails = "/emp/changePasswordMailSend";

  //ticket module
  static ticketList = "/ticket/ticketList";
  static addTicket = "/ticket/addTicket";
  static editTicket = "/ticket/editTicket";
  static deleteTicket = "/ticket/deleteTicket";
  static closeTicket = "/ticket/closeTicket";
  static NoteList = "/notes/NoteList";
  static addNote = "/notes/addNote";
  static deleteNote = "/notes/DeleteNote";
  static deleteFile = "/notes/DeleteFile";
  static EditNote = "/notes/EditNote";
  static reopenTicket = "/ticket/reopenTicket";
  static addCommentReferCandidate = "/notes/addCommentReferCandidate";
  static editCommentReferCandidate = "/notes/editCommentReferCandidate";

  // new-attendence regularization
  static addnewRegularization = "/regularization/add";
  static addnewListRegularization = "/regularization/listing";

  // admin side attendence regularization (approve or deny)
  static getregularization = "/regularization/listing";
  static approveonduty = "/regularization/approve";
  static rejectonduty = "/regularization/reject";

  // Shift Swap / Double Shift / Shift Change (docs/swap-shift) — Phase 6
  // frontend contract. Phase 5 (server/routes/hrms_module/shiftChange.js)
  // has not been built yet, so every call against these will 404/fail until
  // then — expected per the phased build sequencing. `shiftChangePendingApprovals`
  // is a frontend-side addition beyond Phase 5's documented endpoint list
  // (which only defines the employee-facing my-requests list) — see
  // ShiftChangeApprove.js for why the RM/Admin inbox needs its own listing
  // source.
  static shiftChangeRequestCreate = "/shift-change/request";
  static shiftChangeMyRequests = "/shift-change/my-requests";
  static shiftChangeMonthlyCount = "/shift-change/monthly-count";
  static shiftChangePendingApprovals = "/shift-change/pending-approvals";
  // Base path — callers append "/" + requestId + "/eligible-partners" | "/cancel".
  static shiftChangeBase = "/shift-change";
  static shiftChangeExportApprovalReport = "/shift-change/export-report";
  // Unified approve/reject endpoints for a shift_change_request's own _id —
  // work whether that request ended up engine-managed or legacy-managed; the
  // server decides internally which path applies. See ShiftChangeApprove.js /
  // EligiblePartnerPicker.js for the callers.
  static shiftChangeApproveAction = "/shift-change/approve";
  static shiftChangeRejectAction = "/shift-change/reject";

  //Probation module
  static ApproveReject = "/emp/update/status";
  static getProbationData = "/emp/get/probation/extend";
  //notification
  static getNotification = "/leave_master/getNotification";
  static readNotification = "/leave_master/readnotification";
  static readNotificationFromUser = "/leave_master/readAllNotification";

  //change leave status from notification
  static approveLeave = "/leave_master/ApproveLeaveApplicationById";
  static rejectLeaveFromNotification =
    "/leave_master/RejectLeaveApplicationById";
  static approveRegFromNotification = "/employeeReg/acceptregularisationById";

  // performance
  static performanceReview = "/empPerformanceReview/list";
  //Goal Module
  static goalListing = "/goals/listGoals";
  static createdlistGoals = "/goals/createdlistGoals";
  static teamlistGoals = "/goals/teamlistGoals";
  static employeelistGoals = "/goals/employeelistGoals";

  //optional holidays
  static optionalHolidays = "/optionalLeave/getOptionalLeaveByUserId";
  static applyOptHoliday = "/optionalLeave/applyOptionalLeave";

  // goal list with subgoals --- listingWithSubgoals
  static listingWithSubgoals = "/goals/listingWithSubgoals";
  static goalListExport = "/goals/exportGoalsCSV";
  static exportGoalsforcreatedbyCSV = "/goals/exportGoalsforcreatedbyCSV";
  static addGoal = "/goals/addGoal";
  static editGoal = "/goals/editGoal";
  static goalByID = "/goals/goalById";
  static deleteGoal = "/goals/deleteGoal";
  static addWeeklyStatus = "/goals/addWeeklyStatus";
  static subgoalListing = "/subgoals/listsubGoals";
  static subgoalById = "/subgoals/editsubgoal";
  static deleteActionItem = "/goals/deleteActionItem";
  static deleteMilestoneItem = "/goals/deleteMilestoneItem";
  static listWeeklyStatus = "/goals/listWeeklyStatus";
  static editWeeklyStatus = "/goals/editWeeklyStatus";
  static deleteWeeklyStatus = "/goals/deleteWeeklyStatus";
  static addsubgoal = "/subgoals/addSubGoal";
  static listingsubgoalbygoalId = "/subgoals/listingsubgoalbygoalId";
  static editSubGoal = "/subgoals/editsubgoal";
  static getSubGoal = "/subgoals/subgoalsbyId";
  static deleteSubGoal = "/subgoals/deletegoalsbyId";
  static mySubgoalsList = "/subgoals/mySubgoalsList";

  // goal-reports
  static listGoalReports = "/goals/listGoalReports";
  static listTeamGoalReports = "/goals/listTeamGoalReports";

  // Goal Records

  static listGoalRecords = "/goals/listGoalRecords";
  static editRecord = "/goals/editRecord";

  // Performance Cycles
  static performanceCyclesList = "/performanceCycles/list";
  static performanceCyclesCreate = "/performanceCycles";
  static performanceCyclesGet = "/performanceCycles";
  static performanceCyclesUpdate = "/performanceCycles";
  static performanceCyclesDelete = "/performanceCycles";

  // Appraisal Templates
  static appraisalTemplatesList = "/appraisalTemplates/list";
  static appraisalTemplatesCreate = "/appraisalTemplates";
  static appraisalTemplatesGet = "/appraisalTemplates";
  static appraisalTemplatesUpdate = "/appraisalTemplates";
  static appraisalTemplatesDelete = "/appraisalTemplates";

  // Performance Review Instances (Phase 2 - Appraisal workflow)
  static performanceReviewInstancesList = "/performanceReviewInstances/list";
  static performanceReviewInstancesCreate = "/performanceReviewInstances";
  static performanceReviewInstancesCreateForCycle = "/performanceReviewInstances/create-for-cycle";
  static performanceReviewInstancesGet = "/performanceReviewInstances";
  static performanceReviewInstanceSelf = "/performanceReviewInstances";
  static performanceReviewInstanceManager = "/performanceReviewInstances";
  static performanceReviewInstanceHRApprove = "/performanceReviewInstances";
  static performanceReviewInstanceHRSendBack = "/performanceReviewInstances";
  static performanceReviewInstancesStats = "/performanceReviewInstances/stats";
  static performanceReviewInstancesDashboard = "/performanceReviewInstances/dashboard";
  static performanceReviewInstancesExportSummary = "/performanceReviewInstances/export-summary";

  // Appraisal Notes (monthly manager notes)
  static appraisalNotesEmployees = "/employeeAppraisalNotes/employees";
  static appraisalNotesList = "/employeeAppraisalNotes/list";
  static appraisalNotesCreate = "/employeeAppraisalNotes";
  static appraisalNotesUpdate = "/employeeAppraisalNotes";
  static appraisalNotesDelete = "/employeeAppraisalNotes";

  // Development Plans
  static developmentPlansList = "/developmentPlans/list";
  static developmentPlansCreate = "/developmentPlans";
  static developmentPlansGet = "/developmentPlans";
  static developmentPlansUpdate = "/developmentPlans";
  static developmentPlansDelete = "/developmentPlans";

  // Performance Goals (new module)
  static performanceGoalsCreate = "/performanceGoals";
  static performanceGoalsMyList = "/performanceGoals/list/my";
  static performanceGoalsTeamList = "/performanceGoals/list/team";
  static performanceGoalsApprovalsList = "/performanceGoals/list/approvals";
  static performanceGoalsGetById = "/performanceGoals";
  static performanceGoalsUpdate = "/performanceGoals";
  static performanceGoalsDelete = "/performanceGoals";
  static performanceGoalsUpdateProgress = "/performanceGoals";
  static performanceGoalsApprove = "/performanceGoals";
  static performanceGoalsReject = "/performanceGoals";
  static performanceGoalsBulkApprove = "/performanceGoals/bulk-approve";
  static performanceGoalsBulkReject = "/performanceGoals/bulk-reject";

  //Performance KPI
  static addKPI = "/performance-kpi/add";
  static kpiList = "/performance-kpi/list";
  static editKPI = "/performance-kpi/edit";
  static deleteKPI = "/performance-kpi/delete";
  static kpiListForUser = "/performance-kpi/listForUser";
  static updateUserKpi = "/performance-kpi/updateProgress";

  //Inventory
  static addAssestType = "/inventory/addAssestType";
  static AssestTypeDropdownList = "/inventory/AssestTypeDropdownList";
  static AssestTypeList = "/inventory/AssestTypeList";
  static AssestTypeById = "/inventory/AssestTypeById";
  static deleteAssestType = "/inventory/deleteAssestType";
  static editAssestType = "/inventory/editAssestType";
  static AssetChart = "/inventory/AssetsChart";
  static nonAllocatedAssetDetails = "/inventory/nonAllocatedAssestDetails";
  static getAssetTypeInfo = "/inventory/assetsTypeDetailChart";
  // static getAssetHealth = "/inventory/assetsTypeHealthChart";
  static getAssetAge = "/inventory/assetsTypeAgeChart";
  // static getAssetService = "/inventory/assetsTypeReminderChart";
  static disposedAssetList = "/inventory/DisposedAssestList";
  static assetsDetailChart = "/inventory/assetsDetailChart";

  //Achivements
  static achivementLists = "/achievement/achievementList";
  static achivementDelete = "/achievement/deleteAchivement";
  static addAchivement = "/achievement/addAchievement";
  static achievementById = "/achievement/achievementById";

  static exportLeaveListCSV = "/employeeReg/exportLeaveListCSV";
  static exportLeaveApprovalCSV = "/employeeReg/exportLeaveApprovalCSV";
  static exportLeaveBalanceCSV = "/employeeReg/exportLeaveBalanceCSV";
  static exportLeftEmployeeCSV = "/emp/exportLeftempListCSV";
  static exportJoinemployeesListCSV = "/emp/exportJoinemployeesListCSV";

  //ELDI
  static AdminlistingELDI = "/refer-eldi/get";
  static createReferELDI = "/refer-eldi/create";
  static deleteReferELDI = "/refer-eldi/delete";
  static exportDate = "/refer-eldi/export";
  static activity = "/refer-eldi/edit";

  // Assest
  static assetsList = "/inventory/AssestList";
  static assetsTypeDropdownList = "/inventory/AssestTypeDropdownList";
  static addAsset = "/inventory/addAssest";
  static assetById = "/inventory/AssestById";
  static editAsset = "/inventory/editAssest";
  static assetDelete = "/inventory/deleteAssest";
  static assetImportCsv = "/inventory/importAssest";
  static allocateAssest = "/inventory/AllocateAssest";
  static deallocateAssest = "/inventory/DeAllocateAssest";
  static AssestAllocationList = "/inventory/AssestAllocationList";
  static AssestDropdownList = "/inventory/AssestDropdownList";
  static ExportAssestList = "/inventory/ExportAssestList";
  static ExportAssestListForUser = "/inventory/ExportAssestListForUser";
  static ExportAssestTypeList = "/inventory/ExportAssestTypeList";
  static addToScrape = "/inventory/addToScrape";
  static assetHistoryRepairList = "/inventory/AssestRepairList";
  static addRepair = "/inventory/addToRepair";
  static removeRepair = "/inventory/removeFromRepair";
  static addToDispose = "/inventory/addToDispose";

  //experience details
  static listingWExp = "/ExpOfEmp/ExpOfEmpList";
  static addExp = "/ExpOfEmp/addExpOfEmp";
  static editExp = "/ExpOfEmp/updateExpOfEmp";
  static deleteExp = "/ExpOfEmp/deleteExpOfEmp";
  static fetchbyid = "/ExpOfEmp/getExpOfEmpById";

  // Admin Job Openings
  static jobOpeningList = "/jobs/JobsList";
  static addJob = "/jobs/addjob";
  static JobById = "/jobs/JobById";
  static editJob = "/jobs/editjob";
  static deleteJob = "/jobs/deletejob";
  static exportJobsList = "/jobs/ExportJobsList";
  static jobstatuschange = "/jobs/jobstatuschange";
  static jobhiringmethodchange = "/jobs/jobhiringmethodchange";

  //business unit
  static businessUnit = "/businessUnits/BusinessUnitList";
  static addBusinessUnit = "/businessUnits/addBusinessUnit";
  static editBusinessUnit = "/businessUnits/updateBusinessUnit";
  static deleteBusinessUnit = "/businessUnits/deleteBusinessUnit";

  // function/role
  static functionRole = "/functionOrRole/functionsList";

  // sub-department
  static subDepartment = "/subDepartment/subDepartmentDropdownList";
  static addSubDepartment = "/subDepartment/addSubDepartment";
  static updateSubDepartment = "/subDepartment/updateSubDepartment";
  static deleteSubDepartment = "/subDepartment/deleteSubDepartment";
  static listSubDepartments = "/subDepartment/subDepartmentList";

  //refer candidate
  static createJobReference = "/jobReference/createJobReference";
  static listReference = "/jobReference/listJobreferences";
  static jobOpeningListDropdown = "/jobReference/jobOpeningListDropdown";
  static deleteJobReference = "/jobReference/deleteJobReference";
  static getJobReferenceById = "/jobReference/getJobReferenceById";
  static editJobReference = "/jobReference/editJobReference";
  static exportJobReferenceList = "/jobReference/ExportJobReferenceList";

  //pending task list
  static pendingTaskList = "/leave_master/pendingTaskList";

  // job comments
  static addJobComment = "/jobComments/addComment";
  static viewComment = "/jobComments/listComments";
  static editCommentbyId = "/jobComments/commentById";
  static updateComment = "/jobComments/editComment";
  static deleteComment = "/jobComments/deleteComment";

  // app settings
  static getAppSettings = "/app/settings/get";
  static editAppSettings = "/app/settings/edit";

  //new joineees module
  static AddnewJoinee = "/newjoinerDetails/add";
  static AssetsRequired = "/newjoinerDetails/addAssets";
  static Newjoineelist = "/newjoinerDetails/list";
  static getAssets = "/newjoinerDetails/getAssets";

  //onboarding module
  static OnboardingInitiation = "/onboardingEmp/add";
  static uploadDocs = "/onboardingEmp/uploadDocument";
  static submiteveryDoc = "/onboardingEmp/addDocuments";
  static onboardedEmps = "/onboardingEmp/getAllOnboardingEmps";
  static viewdocsList = "/onboardingEmp/documentByOnboardingEmpId";
  static approveRejectdoc = "/onboardingEmp/getAllEmpsDocs";
  static rejection = "/onboardingEmp/rejectedEmpDocs";
  static approval = "/onboardingEmp/approveEmpDocs";
  static submitTarget = "/onboardingEmp/editDocuments";

  static sendNotificationToTL = "/onboardingEmp/sendNotificationToTL";
  static sendDocsToEmp = "/onboardingEmp/sendDocsToEmp";
  static submitDocumentFieldValuesAndSendDocs = "/onboardingEmp/submitDocumentFieldValuesAndSendDocs";
  static editlistnewjoinee = "/newjoinerDetails/editNewJoinerData";
  //SignDocs
  static getOnboardingEmpDetails = "/onboardingEmp/getOnboardingEmp";
  static getOnboardingEmpDetailsByToken =
    "/onboardingEmp/getOnboardingEmpByToken";

  static signDocsByEmployee = "/onboardingEmp/signedDoc";
  static getEmployeeDetailsByToken = "/onboardingEmp/getEmployeeDetailsByToken";
  static editSignDocsByAdmin = "/onboardingEmp/signDocsEditByAdmin";
  static reSignDocEmail = "/onboardingEmp/reSignDocuments";
  static onBoardingToEmplyee = "/onboardingEmp/onBoardingToEmp";
  static sendDeadlineMail = "/onboardingEmp/sendDeadlineMail";
  static OnBoardingEditReportingManager = "/onboardingEmp/edit";
  static editOnboardingEmployeeDetails = "/onboardingEmp/editEmployeeDetails";
  static OnBoardingAddEmpDoc = "/onboardingEmp/addEmpDoc";
  static OnBoardingGetEmpDocs = "/onboardingEmp/getEmpDocs";
  static OnBoardingDeleteEmpDoc = "/onboardingEmp/deleteEmpDoc";


  // Audit module
  static empListForAudit = "/audits/department";
  static getSopTypeByDepartment = "/audits/sop_type/get";
  static viewauditsdata = "/audits/emp/listing";
  static reportslist = "/audits/reports";
  static getSopBySopType = "/audits/sop/get";
  static editSopById = "/audits/sop/edit";
  static deleteSopById = "/audits/sop/delete";
  static addSopByType = "/audits/sop/add";
  static performance = "/audits/performance";
  static headcommentsappend = "/audits/add/head/comments";
  static getHeadComment = "/audits/get/head/comments";

  static addEditCustomSopTypes = "/audits/sop_type/add_edit";
  static deleteCustomSopType = "/audits/sop_type/delete";
  static deleteSpecificCaseName = "/audits/delete/specific-case";

  static getAuditListOfUsers = "/audits/emp/listing";
  static getEmpAuditHistoryList = "/audits/history";
  static getEmpAuditHistoryListforTL = "/audits/historyforTL";
  static getAuditByMonthEmp = "/audits/emp/month_wise";
  static auditUploadFileforSop = "/audits/upload/file";
  static editDeleteAuditFile = "/audits/update/sopfile";

  static addEditNewAudit = "/audits/add";
  static recordsaddEditNewAudit = "/audits/records/add_edit";
  static auditsrecordsget = "/audits/records/get";
  static auditsrecordsdelete = "/audits/records/delete";
  static downloadAuditHistoryPdf = "/audits/history/exportpdf";
  static bulkSopInsert = "/audits/sop/bulk_insert";

  static auditReportPDF = "/audits/AuditReport/pdfExport";
  static empEditListForAudit = "/audits/department/Head";

  //Intern API Route:
  static interntoemp = "/intern/internToEmp";
  static internperformance = "/intern/internPerformance";
  static experienceLetter = "/emp/experienceLetter";
  static relievingLetter = "/emp/relievingLetter";
  static promotionLetter = "/emp/promotionLetter";
  static promotionLetterHistory = "/emp/promotionLetterHistory";
  static promotionLetterReadiness = "/emp/promotionLetterReadiness";
  static internshipLetter = "/emp/internshipLetter";

  // department head
  static depthead = "/audits/department/Head";

  //permission module
  static getResources = "/resources/get";
  static getRoles = "/roles/get";
  static getRolesPermissionList = "/roles/permission-list";
  static getRolesPermissionSummary = "/roles/permission-summary";
  static getRolesPermission = "/roles/permissions";
  static updatePermission = "/roles/permissions";

  static getempRoles = "/roles/get/employee-role";
  static updateempRoles = "/roles/update/employee-role";

  // FRS Security PIN
  static frsPinSetup = "/frs-security/setup-pin";
  static frsPinStatus = "/frs-security/pin-status";
  static frsVerifyPin = "/frs-security/verify-pin";
  static frsMobileVideoUpload = "/frs-security/upload-employee-video";

  static postNewFeedbackData = "/NewEmpFeedbacks/add";
  static getFeedbackStatus = "/NewEmpFeedbacks/get-status";
  static getFeedback = "/NewEmpFeedbacks/get";

  static userRatingMail = "/UserRanking/userRatingchartMail";

  //subscription
  static add_subscription = "/subscription/addSubscription";
  static get_subscription = "/subscription/getSubscriptionsdata";
  static delete_subscription = "/subscription/deleteSubscription";
  static detail_subscription = "/subscription/getDetailSubscription";
  static edit_subscription = "/subscription/updateSubscription";

  //bill-reimbusment-add
  // static add_reimbursement = '/bill/bill-reimbusment-add'
  // static get_reimbursement = '/bill/bill-reimbusment'
  // static detail_reimbursement = '/bill/detail_reimbursement'
  // static update_reimbursement = '/bill/updateReimbursement'
  // static deleteReimbursement = '/bill/deleteReimbursement'
  // static updateReimbursementStatus = '/bill/updateReimbursementStatus'
  // static exportReimbursementsCSV = '/bill/exportReimbursementsCSV'

  static add_reimbursement = "/claim/claim-add";
  static update_reimbursement = "/claim/claim-update";
  // static get_reimbursement
  static detail_reimbursement = '/claim/claim-detail'
  static fetchReimbursements = '/claim/claim-user'
  static fetchRMClaims = '/claim/claim-rm'
  static fetchAdminClaims = '/claim/claim-admin'
  static updateBulkClaims = '/claim/update-multiple-claim-status'
  static downloadReimbursementReceipt = '/claim/claim-receipt'
  static deleteClaim = '/claim/claim-delete'
  static exportClaimCSV = '/claim/claim-export-csv'
  static exportClaimDetailedCSV = '/claim/claim-export-detailed-csv'
  static claimApproveLink = '/claim/claim-approve-link'
  static claimRejectLink = '/claim/claim-reject-link'

  // ── Enhanced Expense Management ─────────────────────────────────────────────
  // Cost Center master
  static expenseCostCenter = "/cost-center"; // POST / , GET / (list) ; append /:id for GET/PUT/DELETE
  // Expense Limit (grade x dept x category -> amount)
  static expenseLimit = "/expense-limit"; // POST / , GET / ; append /:id for GET/PUT/DELETE
  // Expense Config (singleton per company)
  static expenseConfig = "/expense-config"; // GET / , PUT /
  // Advance
  static expenseAdvance = "/expense-advance"; // POST / (issue), GET / (list) ; /:id (detail), /:id/settle
  // Expense (submissions)
  static expense = "/expense"; // POST / (multipart), GET / (list), /:id, PUT /:id, DELETE /:id
  static expenseBalance = "/expense/balance"; // GET /expense/balance/:advanceId
  static expenseApprovalQueue = "/expense/approval-queue"; // GET
  // action on single expense -> PUT /expense/:id/action  { action, comment }
  // Dashboards
  static expenseDashboardEmployee = "/expense-dashboard/employee";
  static expenseDashboardManager = "/expense-dashboard/manager";
  static expenseDashboardFinance = "/expense-dashboard/finance";
  // Reports (each supports ?format=excel|pdf else returns JSON)
  static expenseReport = "/expense-report"; // append /employee-expense , /advance-outstanding , etc.


  //candidateApprovel
  static addCandidateApprovel = "/candidateApprovel/add-candidateApprovel";
  static getCandidateApprovel = "/candidateApprovel/get-candidateApprovel";
  static getCandidateAll = "/candidateApprovel/get-getAllCandidates";
  static getCandidateById = "/candidateApprovel/get-getCandidateById";
  static updateCandidateById = "/candidateApprovel/updateCandidateById";
  static updateCandidateByIdAR = "/candidateApprovel/updateCandidateByIdAR";
  static updateCandidateStatusByadminAR =
    "/candidateApprovel/updateCandidateStatusByadminAR";

  static getAllFormMasters = "/formMaster";
  static getFormMasterById = "/formMaster";
  static getDefaultForm = "/formMaster/default";
  static createFormMaster = "/formMaster";
  static updateFormMaster = "/formMaster";
  static deleteFormMaster = "/formMaster";
  static duplicateFormMaster = "/formMaster";
  static getFields = "/fields";
  static getAllPublicFields = (slug) => `/fields/${slug}/public`;
  /** Authenticated; use for admin edit modal so response includes permissions and user is not logged out. */
  static getOnboardingFormStructure = (slug) => `/fields/${slug}/onboarding-structure`;
  static createtField = "/fields";
  static reorderFields = "/fields/reorder";
  static editField = "/fields";
  static deleteField = "/fields";
  static reorderField = "/fields/reorder";
  static getSections = "/section";
  static editSection = "/section";
  static getPredefinedConfig = "/section/predefined-config";
  static updatePredefinedConfig = "/section/predefined-config";
  static createSection = "/section";
  static deleteSection = "/section";
  static reorderSection = "/section/reorder/all";

  // Exit Feedback form builder (sections + fields + predefined config)
  static getExitFeedbackFields = "/exit-feedback-fields";
  static createExitFeedbackField = "/exit-feedback-fields";
  static editExitFeedbackField = "/exit-feedback-fields";
  static deleteExitFeedbackField = "/exit-feedback-fields";
  static reorderExitFeedbackField = "/exit-feedback-fields/reorder";
  static getExitFeedbackSections = "/exit-feedback-section";
  static createExitFeedbackSection = "/exit-feedback-section";
  static editExitFeedbackSection = "/exit-feedback-section";
  static deleteExitFeedbackSection = "/exit-feedback-section";
  static reorderExitFeedbackSection = "/exit-feedback-section/reorder/all";
  static getExitFeedbackPredefinedConfig = "/exit-feedback-section/predefined-config";
  static updateExitFeedbackPredefinedConfig = "/exit-feedback-section/predefined-config";

  // Candidate Approval form builder (sections + fields + predefined config)
  static caGetSections = "/candidateApprovalSection";
  static caCreateSection = "/candidateApprovalSection";
  static caEditSection = "/candidateApprovalSection";
  static caDeleteSection = "/candidateApprovalSection";
  static caReorderSection = "/candidateApprovalSection/reorder/all";
  static caGetPredefinedConfig = "/candidateApprovalSection/predefined-config";
  static caUpdatePredefinedConfig = "/candidateApprovalSection/predefined-config";
  static caGetFields = "/candidateApprovalFields";
  static caCreateField = "/candidateApprovalFields";
  static caEditField = "/candidateApprovalFields";
  static caDeleteField = "/candidateApprovalFields";
  static caReorderField = "/candidateApprovalFields/reorder";

  // Onboarding Initiation form builder (sections + fields + predefined config)
  static oiGetSections = "/onboardingInitiationSection";
  static oiCreateSection = "/onboardingInitiationSection";
  static oiEditSection = "/onboardingInitiationSection";
  static oiDeleteSection = "/onboardingInitiationSection";
  static oiReorderSection = "/onboardingInitiationSection/reorder/all";
  static oiGetPredefinedConfig = "/onboardingInitiationSection/predefined-config";
  static oiUpdatePredefinedConfig = "/onboardingInitiationSection/predefined-config";
  static oiGetFields = "/onboardingInitiationFields";
  static oiCreateField = "/onboardingInitiationFields";
  static oiEditField = "/onboardingInitiationFields";
  static oiDeleteField = "/onboardingInitiationFields";
  static oiReorderField = "/onboardingInitiationFields/reorder";

  // Employee Master form builder (sections + fields + predefined config)
  static emGetSections = "/employeeMasterSection";
  static emCreateSection = "/employeeMasterSection";
  static emEditSection = "/employeeMasterSection";
  static emDeleteSection = "/employeeMasterSection";
  static emReorderSection = "/employeeMasterSection/reorder/all";
  static emGetPredefinedConfig = "/employeeMasterSection/predefined-config";
  static emUpdatePredefinedConfig = "/employeeMasterSection/predefined-config";
  static emGetFields = "/employeeMasterFields";
  static emCreateField = "/employeeMasterFields";
  static emEditField = "/employeeMasterFields";
  static emDeleteField = "/employeeMasterFields";
  static emReorderField = "/employeeMasterFields/reorder";

  // Rejoin Employee form builder (sections + fields + predefined config)
  static reGetSections = "/rejoinEmployeeSection";
  static reCreateSection = "/rejoinEmployeeSection";
  static reEditSection = "/rejoinEmployeeSection";
  static reDeleteSection = "/rejoinEmployeeSection";
  static reReorderSection = "/rejoinEmployeeSection/reorder/all";
  static reGetPredefinedConfig = "/rejoinEmployeeSection/predefined-config";
  static reUpdatePredefinedConfig = "/rejoinEmployeeSection/predefined-config";
  static reGetFields = "/rejoinEmployeeFields";
  static reCreateField = "/rejoinEmployeeFields";
  static reEditField = "/rejoinEmployeeFields";
  static reDeleteField = "/rejoinEmployeeFields";
  static reReorderField = "/rejoinEmployeeFields/reorder";

  //karma
  static add_karma_reward = "/karma-reward/create";
  static get_karma_reward = "/karma-reward/get";
  static get_karma_reward_user = "/karma-reward/getuser";

  static get_karma_reward_Id = "/karma-reward/get";
  static update_karma_reward_Id = "/karma-reward/update";
  static delete_karma_reward_Id = "/karma-reward/delete";

  //karmahistory
  static get_karma_history = "/send-karma/get";
  static performanceWaffle = "/emp/performance";
  static karmaLeaderboard = "/emp/karmaLeaderboard";
  static getKarmaDetails = "/emp/getKarmaDetails";

  //reward reedem
  static karmaRewardCreate = "/emp/create-redeem";
  static karmaRewardGet = "/emp/get-redeem";
  static karmaRewardUpdate = "/emp/update-redeem";

  //send karma
  static karmaSend = "/send-karma/viahrms";
  static employee = "/send-karma/employee";

  static wafflecategry = "/category-waffle/categories";

  //questionnaire api
  static addQuestionnaire = "/reviewQuestions360/addQuestionnaire";
  static addQuestions = "/reviewQuestions360/addQuestion";
  static getQuestionnaire = "/reviewQuestions360/questionnaireList";
  static getQuestions = "/reviewQuestions360/questionList";
  static updateQuestionnaire = "/reviewQuestions360/editQuestionnaire";
  static updateQuestion = "/reviewQuestions360/editQuestion";
  static deleteQuestionnaire = "/reviewQuestions360/deleteQuestionnaire";
  static deleteQuestion = "/reviewQuestions360/deleteQuestion";
  static deleteMultipleQuestionnaire =
    "/reviewQuestions360/deleteQuestionnaireMany";
  static deleteMultipleQuestions = "/reviewQuestions360/deleteQuestionMany";

  // review apis
  static addReview = "/review360/addReview360";
  static getReviewList = "/review360/getReview360";

  static getReviewById = "/review360/getReview360ById";
  static updateReview = "/review360/updateReview360";
  static deleteReview = "/review360/deleteReview360";

  //survey apis
  static getEmployeeList = "/reviewQuestions360/employeesList";
  static submitReviewAnswers = "/review360Answer/submitReview";

  //analytics api
  static getAnalytics = "/analytics/get";
  static getAnalyticsReport = "/analytics/export";

  //getEmployeesByCategories
  static updateSendingBalance = "/emp/updateSendingBalance";
  static getEmployeesKarmaSendingBalanceByCategories =
    "/emp/getEmployeesKarmaSendingBalanceByCategories";
  static getEmployeesKarmaSendingBalanceByDesignation =
    "/emp/getEmployeesKarmaSendingBalanceByDesignation";
  static updateKarmaBalanceByDesignation =
    "/emp/updateKarmaBalanceByDesignation";

  static addCompanyDocs = "/onboardingMaster/add";

  static updateCompanyDocs = "/onboardingMaster/update";

  static getCompanyDocs = "/onboardingMaster/getAll";
  static deleteCompanyDocs = "/onboardingMaster/delete";
  static deleteCompanyDocField = "/onboardingMaster/delete-field";

  static getCompanyMedia = "/CompanyReg/getCompanyMediaBySlug";

  // Approval workflow configuration
  static getApprovalWorkflowMeta = "/approval-workflow/meta";
  static getApprovalWorkflowList = "/approval-workflow";
  static getApprovalWorkflowByModule = "/approval-workflow/"; // + moduleKey
  static postApprovalWorkflowCapabilities = "/approval-workflow/capabilities";
  static saveApprovalWorkflow = "/approval-workflow";
  static updateApprovalWorkflow = "/approval-workflow/"; // + workflow id
  static deleteApprovalWorkflow = "/approval-workflow/"; // + workflow id
  static previewApprovalWorkflow = "/approval-workflow/preview";
  static toggleApprovalWorkflowEngine = "/approval-workflow/"; // + moduleKey + "/engine"

  // Approval workflow runtime
  static getApprovalCounts = "/approvals/counts";
  static getApprovalPending = "/approvals/pending";
  static getApprovalMyRequests = "/approvals/my-requests";
  static getApprovalDetail = "/approvals/"; // + requestId
  static getApprovalByEntity = "/approvals/by-entity/"; // + moduleKey + "/" + entityId
  static postApprovalAction = "/approvals/"; // + requestId + "/action"
  static postApprovalBulkAction = "/approvals/bulk-action";
  static postApprovalWithdraw = "/approvals/"; // + requestId + "/withdraw"

  //document template crud
  static addDocumentTemplate = "/documentTemplate/addDocumentTemplate";
  static listDocumentTemplate = "/documentTemplate/listDocumentTemplate";
  static editDocumentTemplate = "/documentTemplate/editDocumentTemplate";

  static listDocumentFields = "/documentField/listDocumentFields";
  static addDocumentField = "/documentField/addDocumentField";
  static editDocumentField = "/documentField/editDocumentField";
  static deleteDocumentField = "/documentField/deleteDocumentField";
  static deleteDocumentTemplate = "/documentTemplate/deleteDocumentTemplate";
  static defaultLetterTypes = "/documentTemplate/defaultLetterTypes";
  static defaultLetterHtml = "/documentTemplate/defaultLetterHtml";

  //function role crud
  static getFunctionRoleList = "/functionOrRole/functionsList";
  static addFunctionRole = "/functionOrRole/addFunctions";
  static updateFunctionRole = "/functionOrRole/updateFunctions";
  static deleteFunctionRole = "/functionOrRole/deleteFunctions";
  // static getCompanyMedia = "/CompanyReg/getCompanyMediaBySlug"

  //brnch crud
  static getBranchList = "/officeBranch/branchList";
  static addBrnach = "/officeBranch/addBranch";
  static updateBranch = "/officeBranch/editBranch";
  static deleteBranch = "/officeBranch/deleteBranch";

  //admin dashboard analytics data
  static statisticsData = "/emp/employeeData";
  static graphStatisticsData = "/emp/statistics";

  //today's attendence api
  static todaysAttendence = "/emp/branchData";
  static getGenderData = "/emp/branchwiseGenderCount";

  static getEmployeesRosterList = "/roster/getEmployeesRosterList";
  static getEmployeeRosterCalendar = "/roster/getEmployeeRosterCalendar";
  static importRoster = "/roster/upload-import";
  static addRoster = "/roster/addRoster"; // POST
  static updateRoster = "/roster/updateRoster"; // PUT, caller appends "/" + id
  static deleteRoster = "/roster/deleteRoster"; // DELETE, caller appends "/" + id

  //today's attendence api
  static getNewJoinedEmployees = "/emp/newJoinee";

  // weekly mood
  static addMood = "/moodmeter/addMood";
  static getMood = "/moodmeter/getMood";
  static getMoodAnalytics = "/moodmeter/getMoodAnalytics";
  static getEmployeesMoodList = "/moodmeter/getEmployeeMoodsList";
  static getEmployeesWiseMoodDetails = "/moodmeter/getEmployeeMoodDetails";
  static getAttendenceCountEmployeesWise = "/emp/attendanceTLwise";
  static getMoodChartData = "/moodmeter/getMoodPieChartData";

  static getAttendenceForUserDashboard = "/emp/attendanceDataUserDashboard";
  static getAttendenceForUserDashboardV2 = "/emp/attendanceDataUserDashboardV2";

  /////
  static assigncron = "/cronCompany/assign-default";
  static listcron = "/cronCompany";
  static editcron = "/cronCompany";
  static deletecron = "/cronCompany";
  static run_manual_cron = "/cron";

  // Leave Settings APIs:
  static getLeaveSettings = "/leaveSettings";
  static addLeaveSettings = "/leaveSettings";
  static leaveSettingsV2Rules = "/leave-settings-v2/rules";
  static leaveSettingsV2Balances = "/leave-settings-v2/balances";
  static leaveSettingsV2UpdateBalance = "/leave-settings-v2/balances/adjust";
  static leaveSettingsV2AssignmentsList = "/leave-settings-v2/assignments/list";
  static leaveSettingsV2AssignmentsAssign = "/leave-settings-v2/assignments";
  static leaveSettingsV2AssignmentsImportCsv = "/leave-settings-v2/assignments/import-csv";
  static leaveSettingsV2AssignmentsExportCsv = "/leave-settings-v2/assignments/export-csv";
  static leaveSettingsV2MyOptions = "/leave-settings-v2/employee/my-options";
  static leaveSettingsV2OptionsForEmployee = "/leave-settings-v2/employee/options-for-employee";
  static leaveSettingsV2ValidateApplication = "/leave-settings-v2/employee/validate-application";
  static leaveSettingsV2SubmitApplication = "/leave-settings-v2/employee/submit-application";
  static leaveSettingsV2AdminSubmitApplication = "/leave-settings-v2/admin/submit-application";
  static leaveSettingsV2ImportTemplate = "/leave-settings-v2/balances/import-template";
  static leaveSettingsV2ImportBalances = "/leave-settings-v2/balances/import";
  static leaveBalanceLedgerList = "/leave-settings-v2/balance-ledger/list";
  static leaveBalanceLedgerEmployee = "/leave-settings-v2/balance-ledger/employee";
  static leaveBalanceLedgerExport = "/leave-settings-v2/balance-ledger/export";

  static triggerMonthlyAllocation = "/cron/triggerMonthlyAllocation";
  static triggerYearlyAllocation = "/cron/triggerYearlyAllocation";

  static revertMonthlyAllocation = "/cron/revertMonthlyAllocation";
  static revertYearlyAllocation = "/cron/revertYearlyAllocation";

  //project overview
  static projectOverview = "/v1/maintenance/getEmployeeOverviewData";

  static getSkills = "/skills/getSkills";
  static addSkill = "/skills/addSkill";
  static updateSkill = "/skills/editSkill";
  static deleteSkill = "/skills/deleteSkill";
  static getSkillById = "/skills/getSkillsById";
  static toggleSkillStatus = "/skills/toggleSkillStatus";

  // Skill Categories
  static addSkillCategory = "/skill-categories/addSkillCategory";
  static getSkillCategories = "/skill-categories/getSkillCategories";
  static getSkillCategoryById = "/skill-categories/getSkillCategoryById";
  static editSkillCategory = "/skill-categories/editSkillCategory";
  static deleteSkillCategory = "/skill-categories/deleteSkillCategory";

  // Proficiency Levels
  static addProficiencyLevel = "/proficiency-levels/addProficiencyLevel";
  static getProficiencyLevels = "/proficiency-levels/getProficiencyLevels";
  static getProficiencyLevelById = "/proficiency-levels/getProficiencyLevelById";
  static editProficiencyLevel = "/proficiency-levels/editProficiencyLevel";
  static deleteProficiencyLevel = "/proficiency-levels/deleteProficiencyLevel";

  // Designation Skill Requirements
  static addDesignationSkillRequirement = "/designation-skill-requirements/addRequirement";
  static saveDesignationRequirements = "/designation-skill-requirements/saveRequirementsForDesignation";
  static getRequirementsByDesignation = "/designation-skill-requirements/getRequirementsByDesignation";
  static getAllDesignationSkillRequirements = "/designation-skill-requirements/getAllRequirements";
  static updateDesignationSkillRequirement = "/designation-skill-requirements/updateRequirement";
  static deleteDesignationSkillRequirement = "/designation-skill-requirements/deleteRequirement";

  // Employee Skills
  static assessSkill = "/employee-skills/assessSkill";
  static getEmployeeSkillProfile = "/employee-skills/getEmployeeSkillProfile";
  static deleteEmployeeSkill = "/employee-skills/deleteEmployeeSkill";
  static getSkillsMatrix = "/employee-skills/getSkillsMatrix";
  static getOrgSkillInventory = "/employee-skills/getOrgSkillInventory";

  // Skill Gap Analysis
  static getEmployeeSkillGap = "/skill-gap/getEmployeeSkillGap";
  static getDesignationSkillGap = "/skill-gap/getDesignationSkillGap";
  static getDepartmentSkillGap = "/skill-gap/getDepartmentSkillGap";
  static getOrganizationSkillGap = "/skill-gap/getOrganizationSkillGap";

  // Career Path (Talent Management spec §5). Distinct from getJourneyByEmployee
  // above, which is the unrelated employment-history timeline.
  static addCareerPath = "/career-paths/addCareerPath";
  static getCareerPaths = "/career-paths/getCareerPaths";
  static editCareerPath = "/career-paths/editCareerPath";
  static deleteCareerPath = "/career-paths/deleteCareerPath";
  static toggleCareerPathStatus = "/career-paths/toggleCareerPathStatus";

  static addCareerPathRole = "/career-path-roles/addRole";
  static getCareerPathStructure = "/career-path-roles/getPathStructure";
  static connectCareerPathRoles = "/career-path-roles/connectRoles";
  static disconnectCareerPathRoles = "/career-path-roles/disconnectRoles";
  static saveCareerPathRolePositions = "/career-path-roles/saveRolePositions";
  static removeCareerPathRole = "/career-path-roles/removeRole";

  static assignEmployeeCareerPath = "/employee-career-assignments/assignEmployee";
  static getCareerAssignments = "/employee-career-assignments/getCareerAssignments";
  static getEmployeeCareerPath = "/employee-career-assignments/getEmployeeCareerPath";
  static updateCareerAssignment = "/employee-career-assignments/updateCareerAssignment";
  static deleteCareerAssignment = "/employee-career-assignments/deleteCareerAssignment";

  static addCompetency = "/competencies/addCompetency";
  static getCompetencies = "/competencies/getCompetencies";
  static editCompetency = "/competencies/editCompetency";
  static deleteCompetency = "/competencies/deleteCompetency";
  static toggleCompetencyStatus = "/competencies/toggleCompetencyStatus";

  static assessCompetency = "/employee-competencies/assessCompetency";
  static getEmployeeCompetencyProfile = "/employee-competencies/getEmployeeCompetencyProfile";
  static deleteEmployeeCompetency = "/employee-competencies/deleteEmployeeCompetency";

  static addCourseSkillMap = "/course-skill-maps/addCourseSkillMap";
  static getCourseSkillMaps = "/course-skill-maps/getCourseSkillMaps";
  static updateCourseSkillMap = "/course-skill-maps/updateCourseSkillMap";
  static deleteCourseSkillMap = "/course-skill-maps/deleteCourseSkillMap";

  static saveCareerRoleRequirement = "/career-role-requirements/saveRequirement";
  static getCareerRoleRequirement = "/career-role-requirements/getRequirementByDesignation";

  static getEmployeeCareerReadiness = "/career-readiness/getEmployeeCareerReadiness";
  static getCareerReadinessDashboard = "/career-readiness/getCareerReadinessDashboard";
  static getCareerReadinessOverview = "/career-readiness/getCareerReadinessOverview";
  static getEmployeeCareerGraph = "/career-readiness/getEmployeeCareerGraph";

  static saveTransitionRequirement = "/career-path-transitions/saveTransitionRequirement";
  static getTransitionRequirement = "/career-path-transitions/getTransitionRequirement";
  static deleteTransitionRequirement = "/career-path-transitions/deleteTransitionRequirement";

  static getJourneyByEmployee = "/journey/employee";

  //Activity Log APIs
  static getActivityLogList = "/activityLog/list";
  static getCronRunLogList = "/activityLog/cron/list";
  static getCronAuditEntries = "/activityLog/cron/audit";
  static getActivityLogById = "/activityLog";

  // Linked products (for header launcher – products linked to company)
  static linkedProducts = "/linked-products";

  // ─── LMS Module ────────────────────────────────────────────────────────────
  // Categories
  static lmsGetCategories = "/lms/categories";
  static lmsCreateCategory = "/lms/categories";
  static lmsUpdateCategory = "/lms/categories";   // PUT /:id
  static lmsDeleteCategory = "/lms/categories";   // DELETE /:id

  // Courses
  static lmsGetCourses = "/lms/courses";
  static lmsGetCourseById = "/lms/courses";       // GET /:id
  static lmsCreateCourse = "/lms/courses";
  static lmsUpdateCourse = "/lms/courses";       // PUT /:id
  static lmsDeleteCourse = "/lms/courses";       // DELETE /:id

  // Modules
  static lmsGetModules = "/lms/courses";       // GET /:courseId/modules
  static lmsCreateModule = "/lms/courses";       // POST /:courseId/modules
  static lmsReorderModules = "/lms/courses";       // PUT /:courseId/modules/reorder
  static lmsUpdateModule = "/lms/courses";       // PUT /:courseId/modules/:moduleId
  static lmsDeleteModule = "/lms/courses";       // DELETE /:courseId/modules/:moduleId

  // Lessons
  static lmsCreateLesson = "/lms/courses";       // POST /:courseId/modules/:moduleId/lessons
  static lmsReorderLessons = "/lms/courses";       // PUT /:courseId/modules/:moduleId/lessons/reorder
  static lmsUpdateLesson = "/lms/courses";       // PUT /:courseId/modules/:moduleId/lessons/:lessonId
  static lmsDeleteLesson = "/lms/courses";       // DELETE /:courseId/modules/:moduleId/lessons/:lessonId

  // Assessments
  static lmsGetAssessments = "/lms/assessments";
  static lmsGetAssessmentById = "/lms/assessments";  // GET /:id
  static lmsCreateAssessment = "/lms/assessments";
  static lmsUpdateAssessment = "/lms/assessments";  // PUT /:id
  static lmsDeleteAssessment = "/lms/assessments";  // DELETE /:id
  static lmsCreateQuestion = "/lms/assessments";  // POST /:id/questions
  static lmsUpdateQuestion = "/lms/assessments";  // PUT /:id/questions/:qId
  static lmsDeleteQuestion = "/lms/assessments";  // DELETE /:id/questions/:qId

  // Attempts
  static lmsManageAttempt = "/lms/assessments";   // POST /:id/attempts
  static lmsGetAttempts = "/lms/assessments";   // GET /:id/attempts
  static lmsUnlockAttempt = "/lms/assessments/attempts"; // PUT /:attemptId/unlock
  static lmsUploadAnswer = "/lms/assessments/attempts"; // POST /:attemptId/answers/upload

  // Enrollments
  static lmsGetEnrollments = "/lms/enrollments";
  static lmsGetEnrollmentDetail = "/lms/enrollments";  // GET /:id/detail
  static lmsCreateEnrollment = "/lms/enrollments";
  static lmsCancelEnrollment = "/lms/enrollments";  // PUT /:id/cancel

  // Progress
  static lmsRecordProgress = "/lms/progress";
  static lmsGetProgress = "/lms/progress";

  // Certificates
  static lmsGetCertificates = "/lms/certificates";
  static lmsDownloadCertificate = "/lms/certificates"; // GET /:id/download

  // Dashboard
  static lmsDashboard = "/lms/dashboard";
  static lmsManagerDashboard = "/lms/dashboard";
  static lmsTeamDashboard = "/lms/dashboard/team";

  // Learning Paths
  static lmsGetLearningPaths = "/lms/learning-paths";
  static lmsCreateLearningPath = "/lms/learning-paths";
  static lmsUpdateLearningPath = "/lms/learning-paths";   // PUT /:id
  static lmsDeleteLearningPath = "/lms/learning-paths";   // DELETE /:id
  static lmsEnrollLearningPath = "/lms/learning-paths";   // POST /:id/enroll

  // Auto-Assignment Rules
  static lmsGetAutoRules = "/lms/auto-assignment/rules";
  static lmsCreateAutoRule = "/lms/auto-assignment/rules";
  static lmsUpdateAutoRule = "/lms/auto-assignment/rules";   // PUT /:id
  static lmsDeleteAutoRule = "/lms/auto-assignment/rules";   // DELETE /:id

  // Manual Grading
  static lmsGetPendingAttempts = "/lms/grading/attempts";
  static lmsGetAttemptAnswers = "/lms/grading/attempts";   // GET /:attemptId/answers
  static lmsGradeAttempt = "/lms/grading/attempts";   // PUT /:attemptId/grade

  // Reports
  static lmsEnrollmentReport = "/lms/reports/enrollments";
  static lmsCompletionReport = "/lms/reports/completion";
  static lmsOverdueReport = "/lms/reports/overdue";
  static lmsDeptSummaryReport = "/lms/reports/department-summary";

  // Phase 3 - Settings
  static lmsDataRetentionPolicy = "/lms/settings/data-retention";
  static lmsCertificateTemplate = "/lms/settings/certificate-template";
  static lmsCertificateTemplatePreview = "/lms/settings/certificate-template/preview";

  // Phase 3 - SCORM
  static lmsScormPackages = "/lms/scorm/packages";
  static lmsScormData = "/lms/scorm/data";

  /**
   * Engage — acknowledgement report for one announcement.
   * @param {string} announcementId
   * @param {{ pageNum: number, pageLimit: number, search?: string, type: "acked"|"pending" }} payload
   */
  static announcementAckReport = async (announcementId, payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: `${this.announcementAckReportUrl}/${announcementId}`,
      body: payload,
    });
  };

  static fetchZohoPayrollConfig = async (companyId) => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: `${this.zohoPayrollConfig}/${companyId}`,
    });
  };

  static saveZohoPayrollConfig = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.zohoPayrollConfig,
      body: payload,
    });
  };

  static syncZohoAttendance = async (payload) => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.zohoPayrollAttendanceSync,
      body: payload,
    });
  };

  static fetchApiSettings = async () => {
    return this.makeAPICall({
      methodName: this.getMethod,
      api_url: this.apiSettingsMcpGet,
    });
  };

  static generateApiSettingsMcpKey = async () => {
    return this.makeAPICall({
      methodName: this.postMethod,
      api_url: this.apiSettingsMcpGenerate,
      body: {},
    });
  };

  static buildZohoPayrollConnectUrl = (companyId, redirectToZoho = false, redirectPathAfterOAuth) => {
    const baseUrl = `${this.API_URL}${this.zohoPayrollConnect}`;
    if (!companyId && !redirectToZoho) {
      return baseUrl;
    }
    const queryParts = [];
    if (companyId) {
      queryParts.push(`companyId=${encodeURIComponent(companyId)}`);
    }
    if (redirectToZoho) {
      queryParts.push("redirect=1");
    }
    if (redirectPathAfterOAuth) {
      queryParts.push(`redirectPath=${encodeURIComponent(redirectPathAfterOAuth)}`);
    }
    return queryParts.length ? `${baseUrl}?${queryParts.join("&")}` : baseUrl;
  };

  // Requests currently in flight, keyed by "GET <url>?<params>" - lets
  // multiple components that mount together (Sidebar, Topbar, Dashboard, ...)
  // and request the exact same GET endpoint at the same time share one
  // network call instead of each firing an independent duplicate request.
  static _inFlightGetRequests = new Map();

  // Short-lived response cache for a small allow-list of "read my own mostly-
  // static reference data" endpoints (current employee record, admin/company
  // settings). Several independent layout components - Sidebar, Topbar,
  // Dashboard widgets - each fetch these on mount, but they don't all mount in
  // the same tick (page bootstrap staggers them by seconds), so in-flight
  // sharing alone doesn't dedupe them. A short TTL cache does, without
  // touching any consumer's logic.
  static _recentGetResponses = new Map();
  static _GET_CACHE_TTL_MS = 60000;
  static _GET_CACHE_URL_PATTERNS = ["/emp/getempbyid/", "/adminsettings/adminSetting/"];

  static _isGetCacheEligible(url) {
    return this._GET_CACHE_URL_PATTERNS.some((pattern) => url.includes(pattern));
  }

  // This API uses POST for list/search/dropdown queries (they carry a body) even
  // though they only READ data. A read cannot make the cached GET responses stale,
  // so these must not clear the cache below — otherwise every list/dropdown POST
  // that lands during page bootstrap wipes the cache and the layout components
  // (App, Sidebar, Logo) re-fetch getempbyid / adminSetting all over again.
  // Identical requests fired at the same moment also share one round trip.
  // Anything not listed here keeps the safe default: treated as a write.
  static _READ_ONLY_POST_URL_PATTERNS = [
    "/emp/employeesList",
    "/emp/employeeDirectoryIds",
    "/emp/empdropdownlist",
    "/empdepartment/empdepartmentDropdownList",
    "/empdepartment/empdesignationDropdownList",
    "/businessUnits/BusinessUnitList",
    "/officeBranch/branchList",
    "/empshift/empshiftList",
    "/emp/adminInoutDropdown",
    "/empInOutTime/getEmpInoutTimeAdminV2",
    "/empInOutTime/getEmpInoutCompanyStatsAdminV2",
    "/empInOutTime/viewmultiplepunch",
    "/employeeReg/getRegularisationRequest",
    "/empInOutTime/getEmpInoutTime",
    "/holidays/holidayList",
    "/leave_master/pendingTaskList",
    "/emp/attendanceTLwise",
    "/employeeReg/getEmployeeAttendanceV2",
    "/leave_master/EmployessLeaveList",
    "/empInOutTime/getmultipalpunchByEmpID",
    "/announcement/dashbordlist",
    "/engage/wall/list",
    "/engage/wall/comments",
  ];
  static _inFlightReadOnlyPosts = new Map();

  static _isReadOnlyPost(url) {
    // Most patterns are the whole path (`endsWith`). A few callers append a
    // dynamic id segment after the route (e.g. getEmpInoutTime/<employeeId>),
    // so also match "pattern/" appearing anywhere - the trailing slash still
    // requires the exact route name, not just a matching prefix substring.
    return this._READ_ONLY_POST_URL_PATTERNS.some(
      (pattern) => url.endsWith(pattern) || url.includes(`${pattern}/`)
    );
  }

  // The cache above is write-blind: it was sized for near-simultaneous mount-time
  // reads (Sidebar/Topbar/Dashboard all requesting the same endpoint within the
  // same tick), not for staying valid across a save. A POST/PUT/PATCH/DELETE that
  // succeeds within the 60s window can otherwise be immediately followed by a
  // deliberate refetch (e.g. a modal's Save handler re-reading the employee
  // record) that silently gets served the PRE-edit cached response, clobbering
  // state that was just correctly set from the write's own response - only a
  // full page refresh (which wipes this in-memory Map) shows the fresh data.
  // Any successful write clears the whole cache rather than trying to match it
  // to the specific endpoint(s) it may have affected - the cache only ever holds
  // a couple of entries, so this costs a few extra reads, never stale data.
  static _invalidateGetCache() {
    this._recentGetResponses.clear();
  }

  static async makeAPICall({
    props,
    methodName,
    api_url,
    body,
    params,
    options,
  }) {
    api_url = this.API_URL + api_url;

    // Register the auth/refresh interceptors once (no-op after the first call).
    registerAxiosInterceptors();

    if (methodName === this.getMethod) {
      // If body is provided, convert it to query params
      const config = {};
      if (body && typeof body === "object") {
        config.params = body;
      } else if (params) {
        config.params = params;
      }
      const cacheKey = `${api_url}?${JSON.stringify(config.params || {})}`;
      const cacheEligible = this._isGetCacheEligible(api_url);
      try {
        let requestPromise = this._inFlightGetRequests.get(cacheKey);
        if (!requestPromise) {
          const cached = cacheEligible
            ? this._recentGetResponses.get(cacheKey)
            : null;
          if (cached && cached.expiresAt > Date.now()) {
            requestPromise = Promise.resolve(cached.response);
          } else {
            requestPromise = axios.get(api_url, config);
            this._inFlightGetRequests.set(cacheKey, requestPromise);
            requestPromise
              .then((response) => {
                if (cacheEligible) {
                  this._recentGetResponses.set(cacheKey, {
                    response,
                    expiresAt: Date.now() + this._GET_CACHE_TTL_MS,
                  });
                }
              })
              .catch(() => {})
              .finally(() => {
                this._inFlightGetRequests.delete(cacheKey);
              });
          }
        }
        const response = await requestPromise;
        this.permissionChange(response.data);
        return response;
      } catch (error) {
        if (props && error.response && error.response.status === 401) {
          this.logOut(props);
        }
        return error.response;
      }
    }
    if (methodName === this.postMethod) {
      if (params) {
        api_url = api_url + "/" + params;
      }
      try {
        let response;
        if (!options && this._isReadOnlyPost(api_url)) {
          const postKey = `${api_url}?${JSON.stringify(body ?? null)}`;
          let pendingPost = this._inFlightReadOnlyPosts.get(postKey);
          const isSharedRequest = Boolean(pendingPost);
          if (!pendingPost) {
            pendingPost = axios.post(api_url, body, options);
            this._inFlightReadOnlyPosts.set(postKey, pendingPost);
            pendingPost
              .catch(() => {})
              .finally(() => {
                this._inFlightReadOnlyPosts.delete(postKey);
              });
          }
          response = await pendingPost;
          if (isSharedRequest) {
            // Each caller gets its own copy so one caller mutating the list
            // (sort/push) cannot change what another caller sees.
            response = { ...response, data: JSON.parse(JSON.stringify(response.data)) };
          }
        } else {
          response = await axios.post(api_url, body, options);
          this._invalidateGetCache();
        }
        this.permissionChange(response.data);
        return response;
      } catch (error) {
        if (props && error.response && error.response.status === 401) {
          this.logOut(props);
        }
        return error.response;
      }
    }
    if (methodName === this.putMethod) {
      if (params) {
        api_url = api_url + "/" + params;
      }
      try {
        const response = await axios.put(api_url, body, options);
        this.permissionChange(response.data);
        this._invalidateGetCache();
        return response;
      } catch (error) {
        if (props && error.response && error.response.status === 401) {
          this.logOut(props);
        }
        return error.response;
      }
    }
    if (methodName === this.patchMethod) {
      if (params) {
        api_url = api_url + "/" + params;
      }
      try {
        const response = await axios.patch(api_url, body, options);
        this.permissionChange(response.data);
        this._invalidateGetCache();
        return response;
      } catch (error) {
        if (props && error.response && error.response.status === 401) {
          this.logOut(props);
        }
        return error.response;
      }
    }
    if (methodName === this.deleteMethod) {
      if (params) {
        api_url = api_url + "/" + params;
      }
      try {
        const response = await axios.delete(api_url, { data: body });
        this.permissionChange(response.data);
        this._invalidateGetCache();
        return response;
      } catch (error) {
        if (props && error.response && error.response.status === 401) {
          this.logOut(props);
        }
        return error.response;
      }
    }
  }

  static logOut(props) {
    props.logOutHandler();
    props.history.push("/login");
  }

  static uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  static permissionChange(data) {
    try {
      if (isPublicCandidateRoute(window.location.pathname)) return;

      // Only a genuinely successful response carries real role/permission
      // data. An error response (wrong password, validation failure,
      // SESSION_LIMIT_REACHED, or literally any other app-level failure)
      // has no `role_id`/`permissions` at all - comparing that absence
      // against whatever is already in localStorage looks exactly like
      // "the user's role was just removed" and force-logs-out this browser,
      // wiping the shared localStorage that every other open tab of the
      // same account also relies on (so unrelated tabs suddenly appear
      // logged out too, even though nothing about their own session ever
      // failed).
      if (!data || data.status !== 200) return;

      let storage_permission = null;
      let storage_role_id = null;
      try {
        storage_permission = JSON.parse(localStorage.getItem("resourceAccess"));
        storage_role_id = JSON.parse(localStorage.getItem("user_data"));
      } catch (e) {
        return;
      }
      const permissions = data.permissions || [];

      function arraysAreEqual(arr1, arr2) {
        const arr1Map = new Map(arr1?.map((item) => [item._id, item]));
        const arr2Map = new Map(arr2?.map((item) => [item._id, item]));
        for (let [key, value] of arr1Map) {
          const arr2Value = arr2Map.get(key);
          if (
            !arr2Value ||
            value.name !== arr2Value.name ||
            value.read !== arr2Value.read ||
            value.create !== arr2Value.create ||
            value.update !== arr2Value.update ||
            value.delete !== arr2Value.delete
          ) {

            return false;
          }
        }

        return true;
      }

      //   if (!window.location.pathname.includes("feedback") && !arraysAreEqual(permissions, storage_permission) || (storage_role_id && storage_role_id.role_id._id !== data.role_id)) {
      //     let companyDomain = localStorage.getItem("companyDomain")
      //     localStorage.clear();
      //     setTimeout(() => {
      //       localStorage.setItem("companyDomain",companyDomain)
      //     }, 1000);
      //     window.location.reload();
      //   }
      // } catch (error) {
      //   console.log(error);
      // }
      // Permission changes must NOT log the user out: refresh the cached
      // permissions in place so screens re-evaluate on next render/navigation.
      if (
        Array.isArray(data.permissions) &&
        data.permissions.length &&
        Array.isArray(storage_permission) &&
        !window.location.pathname.includes("feedback") &&
        !arraysAreEqual(permissions, storage_permission)
      ) {
        localStorage.setItem("resourceAccess", JSON.stringify(data.permissions));
      }
    } catch (error) {
      console.log(error);
    }
  }

  //   static permissionChange(data) {
  //   try {
  //     const storage_permission = JSON.parse(localStorage.getItem("resourceAccess"));
  //     const storage_role_id = JSON.parse(localStorage.getItem("user_data"));
  //     const permissions = data.permissions || [];

  //     function arraysAreEqual(arr1, arr2) {
  //       if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  //       const arr1Map = new Map(arr1.map(item => [item._id, item]));
  //       const arr2Map = new Map(arr2.map(item => [item._id, item]));
  //       for (let [key, value] of arr1Map) {
  //         const arr2Value = arr2Map.get(key);
  //         if (
  //           !arr2Value ||
  //           value.name !== arr2Value.name ||
  //           value.read !== arr2Value.read ||
  //           value.create !== arr2Value.create ||
  //           value.update !== arr2Value.update ||
  //           value.delete !== arr2Value.delete
  //         ) {
  //           return false;
  //         }
  //       }
  //       return true;
  //     }

  //     // Only run the check if both are present
  //     if (
  //       storage_permission &&
  //       storage_role_id &&
  //       !window.location.pathname.includes("feedback") &&
  //       (!arraysAreEqual(permissions, storage_permission) ||
  //         (storage_role_id.role_id && storage_role_id.role_id._id !== data.role_id))
  //     ) {
  //       let companyDomain = localStorage.getItem("companyDomain");
  //       localStorage.clear();
  //       setTimeout(() => {
  //         localStorage.setItem("companyDomain", companyDomain);
  //       }, 1000);
  //       window.location.reload();
  //     }
  //     // If either is missing, do NOT log out, just set them
  //     if (!storage_permission) {
  //       localStorage.setItem("resourceAccess", JSON.stringify(permissions));
  //     }
  //     if (!storage_role_id) {
  //       localStorage.setItem("user_data", JSON.stringify({ role_id: { _id: data.role_id } }));
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  // static permissionChange(data) {
  //   console.log("permissionChange CALLED", data);
  //   try {
  //     console.log("permissionChange typeof data:", typeof data, data);
  //     // Defensive: log before any other code
  //     console.log("permissionChange received data:", data);
  //     const storage_permission = JSON.parse(localStorage.getItem("resourceAccess"));
  //     const storage_role_id = JSON.parse(localStorage.getItem("user_data"));
  //     const permissions = data.permissions || [];

  //     // Add debug logging
  //     console.log("API permissions:", permissions);
  //     console.log("localStorage permissions:", storage_permission);

  //     function compareAndLogPermissions(apiPermissions, localPermissions) {
  //       console.log("🔍 Starting permission comparison...");
  //       console.log("API permissions count:", apiPermissions?.length);
  //       console.log("Local permissions count:", localPermissions?.length);

  //       if (!Array.isArray(apiPermissions) || !Array.isArray(localPermissions)) {
  //         console.log("Invalid permission arrays");
  //         console.log("API permissions is array:", Array.isArray(apiPermissions));
  //         console.log("Local permissions is array:", Array.isArray(localPermissions));
  //         return false;
  //       }

  //       const apiMap = new Map(apiPermissions.map(item => [item._id, item]));
  //       const localMap = new Map(localPermissions.map(item => [item._id, item]));

  //       let hasChanges = false;
  //       let changeCount = 0;

  //       console.log("🔍 Checking for new and changed permissions...");

  //       // Check permissions that exist in API
  //       for (let [id, apiPerm] of apiMap) {
  //         const localPerm = localMap.get(id);

  //         if (!localPerm) {
  //           console.log(`🆕 NEW PERMISSION: ${apiPerm.name} (${id})`);
  //           console.log(`   API: read=${apiPerm.read}, create=${apiPerm.create}, update=${apiPerm.update}, delete=${apiPerm.delete}`);
  //           hasChanges = true;
  //           changeCount++;
  //           continue;
  //         }

  //         // Compare each permission type
  //         const changes = [];
  //         if (apiPerm.read !== localPerm.read) {
  //           changes.push(`read: localStorage=${localPerm.read} → API=${apiPerm.read}`);
  //         }
  //         if (apiPerm.create !== localPerm.create) {
  //           changes.push(`create: localStorage=${localPerm.create} → API=${apiPerm.create}`);
  //         }
  //         if (apiPerm.update !== localPerm.update) {
  //           changes.push(`update: localStorage=${localPerm.update} → API=${apiPerm.update}`);
  //         }
  //         if (apiPerm.delete !== localPerm.delete) {
  //           changes.push(`delete: localStorage=${localPerm.delete} → API=${apiPerm.delete}`);
  //         }

  //         if (changes.length > 0) {
  //           console.log(`🔄 PERMISSION CHANGED: ${apiPerm.name} (${id})`);
  //           changes.forEach(change => console.log(`   ${change}`));
  //           hasChanges = true;
  //           changeCount++;
  //         }
  //       }

  //       console.log("🔍 Checking for removed permissions...");

  //       // Check for permissions that were removed (exist in localStorage but not in API)
  //       for (let [id, localPerm] of localMap) {
  //         if (!apiMap.has(id)) {
  //           console.log(`❌ REMOVED PERMISSION: ${localPerm.name} (${id})`);
  //           console.log(`   Was: read=${localPerm.read}, create=${localPerm.create}, update=${localPerm.update}, delete=${localPerm.delete}`);
  //           hasChanges = true;
  //           changeCount++;
  //         }
  //       }

  //       console.log(`🔍 Comparison complete. Has changes: ${hasChanges}, Total changes: ${changeCount}`);
  //       return hasChanges;
  //     }

  //     function arraysAreEqual(arr1, arr2) {
  //       if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
  //         console.log("arraysAreEqual: One or both inputs are not arrays");
  //         return false;
  //       }

  //       if (arr1.length !== arr2.length) {
  //         console.log(`arraysAreEqual: Length mismatch - arr1: ${arr1.length}, arr2: ${arr2.length}`);
  //         return false;
  //       }

  //       const arr1Map = new Map(arr1.map(item => [item._id, item]));
  //       const arr2Map = new Map(arr2.map(item => [item._id, item]));

  //       if (arr1Map.size !== arr2Map.size) {
  //         console.log(`arraysAreEqual: Map size mismatch - arr1Map: ${arr1Map.size}, arr2Map: ${arr2Map.size}`);
  //         return false;
  //       }

  //       for (let [key, value] of arr1Map) {
  //         const arr2Value = arr2Map.get(key);
  //         if (
  //           !arr2Value ||
  //           value.name !== arr2Value.name ||
  //           value.read !== arr2Value.read ||
  //           value.create !== arr2Value.create ||
  //           value.update !== arr2Value.update ||
  //           value.delete !== arr2Value.delete
  //         ) {
  //           console.log(`arraysAreEqual: Mismatch found for key ${key}`);
  //           console.log("Value 1:", value);
  //           console.log("Value 2:", arr2Value);
  //           return false;
  //         }
  //       }

  //       return true;
  //     }

  //     console.log("🔍 Checking for permission and role mismatches...");

  //     const permissionsMismatch = !arraysAreEqual(permissions, storage_permission);
  //     const roleMismatch = storage_role_id && data.role_id && storage_role_id.role_id._id !== data.role_id;

  //     console.log("Permissions mismatch:", permissionsMismatch);
  //     console.log("Role mismatch:", roleMismatch);
  //     console.log("Current pathname:", window.location.pathname);
  //     console.log("Is feedback page:", window.location.pathname.includes("feedback"));

  //     if (
  //       !window.location.pathname.includes("feedback") &&
  //       (permissionsMismatch || roleMismatch)
  //     ) {
  //       if (permissionsMismatch) {
  //         console.log("🚨 PERMISSIONS MISMATCH DETECTED:");
  //         console.log("=====================================");
  //         const hasChanges = compareAndLogPermissions(permissions, storage_permission);
  //         console.log("Detailed changes logged above ☝️");
  //         console.log("=====================================");
  //       }

  //       if (roleMismatch) {
  //         console.log("🚨 ROLE MISMATCH DETECTED:");
  //         console.log("Role change: localStorage=", storage_role_id && storage_role_id.role_id && storage_role_id.role_id._id, "→ API=", data.role_id);
  //       }

  //       console.log("Permissions or role mismatch detected. Reloading the page.");
  //       // Uncomment the line below if you want to actually reload the page
  //       // window.location.reload();
  //     } else {
  //       console.log("✅ No action needed - either on feedback page or no mismatches detected");
  //     }
  //   } catch (error) {
  //     console.log("permissionChange ERROR", error);
  //     console.error("Full error details:", error);
  //   }
  // }
}
